import { Parse } from "parse";
import { CustomUser } from "../classes";
import calculateFinalAmount from "../Utilities/mintPercentage";
import {
  CAPTCHA_ACTIONS,
  isRecaptchaConfigured,
  requestCaptchaReset,
  takePendingTicket,
} from "../Utilities/recaptcha";
import { sendWebSocketMessage } from "./socket";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

// Export currentPageUsers so other files can access it
export const currentPageUsers = new Set();

export const dataProvider = {
  create: async (resource, params) => {
    // console.log("&&&&& create redeem", params.data);

    try {
      var Resource = null;
      if (resource === "users") {
        const sessionToken = Parse.User.current().getSessionToken();
        const data = (({ username, name, email, password, phone }) => ({
          username,
          name,
          email,
          password,
          phone,
        }))(params.data);
        data.email = data?.email?.toLowerCase();
        data.publicEmail = data?.email;
        const { role, manager } = params.data;
        // console.log("Data", params.data);
        // console.log("data", data);

        // Check if a user already exists with the same username, email, or phone
        const UserQuery = new Parse.Query(CustomUser);
        UserQuery.equalTo("username", data.username);
        const existingUsername = await UserQuery.first();

        const PhoneQuery = new Parse.Query(CustomUser);
        PhoneQuery.equalTo("phone", data.phone);
        const existingPhone = await PhoneQuery.first();

        if (existingUsername) {
          throw new Error("Username is already in use.");
        }
        if (data?.phone && existingPhone) {
          throw new Error("Phone number is already in use.");
        }

        const user = new CustomUser();
        const roleName = await user.setRole(role);
        roleName === "Player"
          ? (data.isPlayer = true)
          : (data.isPlayer = false);


        if (data.isPlayer) {
          // Email is mandatory for players — the same rule is enforced again in
          // the backend's _User beforeSave guard.
          if (!data.email) {
            throw new Error("Email is required to create a player.");
          }

          // _User.email is a protected field in Parse Server, so querying on it
          // without the master key fails with "Permission denied".
          const EmailQuery = new Parse.Query(CustomUser);
          EmailQuery.equalTo("email", data.email);
          const existingEmail = await EmailQuery.first({ useMasterKey: true });
          if (existingEmail) {
            throw new Error("Email is already in use.");
          }

          // reCAPTCHA v3: the verification panel in the Create User form has
          // already scored the agent on the server; its single-use ticket is
          // consumed by the backend's _User beforeSave guard.
          if (isRecaptchaConfigured()) {
            const captchaTicket = takePendingTicket(
              CAPTCHA_ACTIONS.CREATE_PLAYER
            );
            if (!captchaTicket) {
              throw new Error(
                "Please complete the reCAPTCHA verification before creating the player."
              );
            }
            data.captchaTicket = captchaTicket;
          }
        }

        let result;
        try {
          await user.setManager(manager);
          result = await user.signUp(data); //consider changing it to user.save() instead to prevent auto login
        } catch (error) {
          // The ticket may already have been burned; get the form a new one.
          if (data.captchaTicket) {
            requestCaptchaReset(CAPTCHA_ACTIONS.CREATE_PLAYER);
          }
          throw error;
        }
        await result.setManagerACL();
        await result.addToRole();
        await Parse.User.logOut().then((result) =>
          Parse.User.become(sessionToken)
        );
        return { data: { id: result.id, ...result.attributes } };
      } else if (resource === "rechargeRecords") {
        const response = await Parse.Cloud.run("checkPlayingUser", {
          userId: params.data.id,
        });

        // Step 1: Check if user is currently playing
        if (response?.isPlayingNow === "yes") {
          throw new Error(
            "Player must be in lobby screen to perform this action"
          );
        }

        // Step 2: Create and validate transaction details
        const Resource = Parse.Object.extend("Transactions");
        const rechargeTransaction = new Resource();
        const toRole = params.data.role;

        const amount = Number(params.data.amount);
        if (isNaN(amount) || amount <= 0) {
          throw new Error("Amount must be a positive number.");
        }

        const conversionRate = await getConversionRate(toRole);

        rechargeTransaction.set("txn_type", params.data.txn_type);
        rechargeTransaction.set("amount", amount);
        rechargeTransaction.set("remarks", params.data.remarks);
        rechargeTransaction.set("manager", params.data.manager);
        rechargeTransaction.set("username", params.data.username);
        rechargeTransaction.set("user", params.data.id);
        rechargeTransaction.set("conversionRate", conversionRate);

        // reCAPTCHA v3: the verification panel in the Recharge dialog already
        // scored the agent on the server; the Transactions beforeSave guard
        // consumes this single-use ticket.
        if (isRecaptchaConfigured()) {
          const captchaTicket = takePendingTicket(CAPTCHA_ACTIONS.RECHARGE);
          if (!captchaTicket) {
            throw new Error(
              "Please complete the reCAPTCHA verification before recharging."
            );
          }
          rechargeTransaction.set("captchaTicket", captchaTicket);
        }

        // Step 3: Final amount calculation
        try {
          const finalAmount = calculateFinalAmount(amount, conversionRate);
          if (finalAmount !== null) {
            rechargeTransaction.set("finalAmount", finalAmount);
          } else {
            console.warn(
              "Final amount calculation returned null. Not storing it."
            );
          }
        } catch (error) {
          console.error("Error calculating final amount:", error);
          // Handle the error appropriately, e.g., set a default value, return an error in the API response, etc.
          // Example: rechargeTransaction.set("finalAmount", 0); // Or some other default.
          // Example: throw new Error("Failed to calculate final amount.");
        }

        // Step 4: Save transaction and emit WebSocket
        try {
          const result = await rechargeTransaction.save(null, {
            useMasterKey: true,
          });

          // Fetch user balance after recharge
          const userQuery = new Parse.Query(Parse.User);
          const user = await userQuery.get(params.data.id, {
            useMasterKey: true,
          });

          const currentBalance = user.get("balance");

          // Step 5: Emit WebSocket event with fallback
          try {
            sendWebSocketMessage("message",JSON.stringify({
                eventName: "user_current_balance",
                txn_type: params.data.txn_type,
                balance: currentBalance,
                amount: amount,
                userId: params.data.id,
            }));
          } catch (socketError) {
            console.error("WebSocket emit failed:", socketError);
            throw new Error("Recharge failed due to WebSocket error.");
          }

          return { data: { id: result.id, ...result.attributes } };
        } catch (err) {
          console.error("Recharge process failed:", err);
          // The ticket may already have been burned; get the form a new one.
          requestCaptchaReset(CAPTCHA_ACTIONS.RECHARGE);
          throw new Error(err.message || "Recharge failed. Please try again.");
        }
      } else if (resource === "redeemRecords") {
        const response = await Parse.Cloud.run("checkPlayingUser", {
          userId: params.data.id,
        });

        // If user is currently playing, return an error
        if (response?.isPlayingNow === "yes") {
          throw new Error(
            "Player must be in lobby screen to perform this action"
          );
        }

        const amount = Number(params.data.amount);
        if (isNaN(amount) || amount <= 0) {
          throw new Error("Amount must be a positive number.");
        }

        const userQuery = new Parse.Query(Parse.User);
        const user = await userQuery.get(params.data.id, {
          useMasterKey: true,
        });

        const currentBalance = user.get("balance");

        if (amount > currentBalance) {
          throw new Error("Not enough balance to Redeem");
        }

        Resource = Parse.Object.extend("Transactions");
        const rechargeTransaction = new Resource();

        rechargeTransaction.set("txn_type", params.data.txn_type);
        rechargeTransaction.set("amount", amount);
        rechargeTransaction.set("remarks", params.data.remarks);
        rechargeTransaction.set("manager", params.data.manager);
        rechargeTransaction.set("username", params.data.username);
        rechargeTransaction.set("user", params.data.id);

        // reCAPTCHA v3: the verification panel in the Redeem dialog already
        // scored the agent on the server; the Transactions beforeSave guard
        // consumes this single-use ticket.
        if (isRecaptchaConfigured()) {
          const captchaTicket = takePendingTicket(CAPTCHA_ACTIONS.REDEEM);
          if (!captchaTicket) {
            throw new Error(
              "Please complete the reCAPTCHA verification before redeeming."
            );
          }
          rechargeTransaction.set("captchaTicket", captchaTicket);
        }

        try {
          const result = await rechargeTransaction.save(null, {
            useMasterKey: true,
          });

          // Emit WebSocket event
          try {
            sendWebSocketMessage("message",JSON.stringify({
                eventName: "user_current_balance",
                txn_type: params.data.txn_type,
              balance: currentBalance-amount,
                amount: amount,
                userId: params.data.id,
            }));
          } catch (socketError) {
            console.error("WebSocket emit failed:", socketError);
            throw new Error("Redeem failed due to WebSocket error.");
          }

          return { data: { id: result.id, ...result.attributes } };
        } catch (err) {
          console.error("Redeem process failed:", err);
          // The ticket may already have been burned; get the form a new one.
          requestCaptchaReset(CAPTCHA_ACTIONS.REDEEM);
          throw new Error(err.message || "Redeem failed. Please try again.");
        }
      } else if (resource === "gameRtp") {
        const rawData = params.data;

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.reelName) || rawData.reelName.length === 0) {
          throw new Error("Invalid or missing reelName array.");
        }

        // Check if the gameName already exists in the GameRtp table
        const GameRtp = Parse.Object.extend("GameRtp");
        const gameRtpQuery = new Parse.Query(GameRtp);
        gameRtpQuery.equalTo("gameName", rawData?.gameName);

        const existingGameRtp = await gameRtpQuery.first();

        if (existingGameRtp) {
          // If gameName already exists, throw an error
          throw new Error("Game RTP with this gameName already exists.");
        }

        // Define the GameRtp object if it doesn't exist
        const gameRtpObject = new GameRtp();

        // Set gameName and gameRtp to the GameRtp object
        gameRtpObject.set("gameName", rawData?.gameName);
        gameRtpObject.set("gameRtp", rawData?.gameRtp);
        gameRtpObject.set("reelName", rawData?.reelName);

        // Reference gameId from GameCatalogue
        if (rawData.gameId) {
          const GameCatalogue = Parse.Object.extend("GameCatalogue");
          const gameCatalogueQuery = new Parse.Query(GameCatalogue);

          try {
            const gameCatalogueObject = await gameCatalogueQuery.get(
              rawData?.gameId
            );

            if (!gameCatalogueObject) {
              throw new Error("Invalid gameId: GameCatalogue entry not found.");
            }
            gameRtpObject.set("gameId", gameCatalogueObject);
          } catch (error) {
            throw new Error("Invalid gameId: GameCatalogue entry not found.");
          }
        }

        try {
          // Save the GameRtp object
          const result = await gameRtpObject.save();
          return { data: { id: result.id, ...result.attributes } };
        } catch (error) {
          throw error;
        }
      } else if (resource === "firelinkGameRtp") {
        const rawData = params.data;

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.firelinkReelName) || rawData.firelinkReelName.length === 0) {
          throw new Error("Invalid or missing firelinkReelName array.");
        }

        // Check if the gameName and gameRtp combination already exists in the GameRtp table
        const GameRtp = Parse.Object.extend("GameRtp");
        const gameRtpQuery = new Parse.Query(GameRtp);
        gameRtpQuery.equalTo("gameName", rawData?.gameName);
        gameRtpQuery.equalTo("gameRtp", rawData?.gameRtp);

        const existingGameRtp = await gameRtpQuery.first();

        if (existingGameRtp) {
          // If gameName and gameRtp combination already exists, throw an error
          throw new Error(`Firelink Game RTP ${rawData.gameRtp}% already exists for this game.`);
        }

        // Define the GameRtp object (same class as regular games)
        const gameRtpObject = new GameRtp();

        // Set gameName, gameRtp and firelinkReelName to the GameRtp object
        gameRtpObject.set("gameName", rawData?.gameName);
        gameRtpObject.set("gameRtp", rawData?.gameRtp);
        gameRtpObject.set("firelinkReelName", rawData?.firelinkReelName); // Different column for Firelink
        gameRtpObject.set("isActive", true);

        // Reference gameId from GameCatalogue
        if (rawData.gameId) {
          const GameCatalogue = Parse.Object.extend("GameCatalogue");
          const gameCatalogueQuery = new Parse.Query(GameCatalogue);

          try {
            const gameCatalogueObject = await gameCatalogueQuery.get(
              rawData?.gameId
            );

            if (!gameCatalogueObject) {
              throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}`);
            }
            gameRtpObject.set("gameId", gameCatalogueObject);
          } catch (error) {
            console.error("GameCatalogue lookup error:", error);
            throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}. Error: ${error.message}`);
          }
        } else {
          throw new Error("gameId is required for Firelink Game RTP creation.");
        }

        try {
          // Save the GameRtp object with firelinkReelName column
          const result = await gameRtpObject.save();
          return { data: { id: result.id, ...result.attributes } };
        } catch (error) {
          console.error("Firelink Game RTP save error:", error);
          throw new Error(`Failed to save Firelink Game RTP: ${error.message}`);
        }
      } else if (resource === "kenoGameRtp") {
        const rawData = params.data;

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.kenoReelName) || rawData.kenoReelName.length === 0) {
          throw new Error("Invalid or missing kenoReelName array.");
        }

        // Check if the gameName and gameRtp combination already exists in the GameRtp table
        const GameRtp = Parse.Object.extend("GameRtp");
        const gameRtpQuery = new Parse.Query(GameRtp);
        gameRtpQuery.equalTo("gameName", rawData?.gameName);
        gameRtpQuery.equalTo("gameRtp", rawData?.gameRtp);

        const existingGameRtp = await gameRtpQuery.first();

        if (existingGameRtp) {
          // If gameName and gameRtp combination already exists, throw an error
          throw new Error(`Keno Game RTP ${rawData.gameRtp}% already exists for this game.`);
        }

        // Define the GameRtp object (same class as regular games)
        const gameRtpObject = new GameRtp();

        // Set gameName, gameRtp and kenoReelName to the GameRtp object
        gameRtpObject.set("gameName", rawData?.gameName);
        gameRtpObject.set("gameRtp", rawData?.gameRtp);
        gameRtpObject.set("kenoReelName", rawData?.kenoReelName); // Different column for Keno
        gameRtpObject.set("reelName", []); // Set empty array to satisfy schema requirement
        gameRtpObject.set("isActive", true);

        // Reference gameId from GameCatalogue
        if (rawData.gameId) {
          const GameCatalogue = Parse.Object.extend("GameCatalogue");
          const gameCatalogueQuery = new Parse.Query(GameCatalogue);

          try {
            const gameCatalogueObject = await gameCatalogueQuery.get(
              rawData?.gameId
            );

            if (!gameCatalogueObject) {
              throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}`);
            }
            gameRtpObject.set("gameId", gameCatalogueObject);
          } catch (error) {
            console.error("GameCatalogue lookup error:", error);
            throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}. Error: ${error.message}`);
          }
        } else {
          throw new Error("gameId is required for Keno Game RTP creation.");
        }

        try {
          // Save the GameRtp object with kenoReelName column
          const result = await gameRtpObject.save();
          return { data: { id: result.id, ...result.attributes } };
        } catch (error) {
          console.error("Keno Game RTP save error:", error);
          throw new Error(`Failed to save Keno Game RTP: ${error.message}`);
        }
      } else if (resource === "gameCatalogue") {
        const { name, category, description, type, gameSequence, building } =
          params.data;

        // Check for missing fields one by one and throw specific errors
        if (!name) {
          throw new Error("Game name is required.");
        }
        if (!category) {
          throw new Error("Category is required.");
        }

        // Check if a game with the same name already exists
        const GameCatalogue = Parse.Object.extend("GameCatalogue");
        const query = new Parse.Query(GameCatalogue);
        query.equalTo("name", name);

        const existingGame = await query.first();
        if (existingGame) {
          throw new Error("This Game already exists.");
        }

        // Create a new GameCatalogue object
        const gameCatalogueObject = new GameCatalogue();
        gameCatalogueObject.set("name", name);
        gameCatalogueObject.set("category", category);
        gameCatalogueObject.set("description", description);
        gameCatalogueObject.set("type", type);
        gameCatalogueObject.set("gameSequence", Number(gameSequence));

        if (building) {
          const BuildingId = Parse.Object.extend("VegasBuilding");
          const buildingPointer = new BuildingId();
          buildingPointer.id = building;
          gameCatalogueObject.set("building", buildingPointer);
        }

        const result = await gameCatalogueObject.save();
        return { data: { id: result.id, ...result.attributes } };
      } else if (resource === "wheelSpins") {
        const { day, minValue, maxValue, countZero, slots } = params.data;

        if (!day) {
          throw new Error("Day is required.");
        }

        // Check if the day already exists in the SpinningWheel table
        const SpinWheel = Parse.Object.extend("SpinningWheel");
        const spinningWheelQuery = new Parse.Query(SpinWheel);
        spinningWheelQuery.equalTo("day", day);

        const existingSpinningWheel = await spinningWheelQuery.first();

        if (existingSpinningWheel) {
          throw new Error("This Day already exists.");
        }

        const spinWheelObject = new SpinWheel();

        // Set day and slots to the SpinnWheel object
        spinWheelObject.set("day", day);
        spinWheelObject.set("minValue", minValue);
        spinWheelObject.set("maxValue", maxValue);
        spinWheelObject.set("countZero", countZero);
        spinWheelObject.set("slots", slots);

        try {
          // Save the SpinWheel object
          const result = await spinWheelObject.save();
          return { data: { id: result.id, ...result.attributes } };
        } catch (error) {
          throw error;
        }
      } else {
        Resource = Parse.Object.extend(resource);
      }
      const query = new Resource();
      const result = await query.save(params.data);
      // console.log(result);
      return { data: { id: result.id, ...result.attributes } };
    } catch (error) {
      throw error;
    }
  },
  getOne: async (resource, params) => {
    //works
    var query = null;
    var result = null;
    try {
      if (resource === "users") {
        query = new Parse.Query(Parse.User);
        result = await query.get(params.id, { useMasterKey: true });
      } else if (resource === "GameCatalogue") {
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        query.include("building");

        result = await query.get(params.id);

        const record = {
          id: result.id,
          name: result.get("name"),
          category: result.get("category"),
          description: result.get("description"),
          gameSequence: result.get("gameSequence"),
          building: result.get("building")?.id || null,
          comingSoon: result.get("comingSoon"),
          comingSoonImage: result.get("comingSoonImage"),
        };

        return { data: record };
      } else {
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        result = await query.get(params.id);
      }
      // console.log("GETONE CALLED");
      // console.log(params);
      // console.log(result, result.attributes);
      return { data: { id: result.id, ...result.attributes } };
    } catch (error) {
      return error;
    }
  },
  getList: async (resource, params) => {
    //works
    Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const filter = params.filter;
    var query = null;
    var count = null;
    console.log(filter,"Filter");

    if (resource === "users") {
      
      console.log("manager", "managermanager", filter);

      query = new Parse.Query(Parse.User);
      query.descending("createdAt");
      query.include("role");

      // Exclude currently logged-in user
      const currentUser = Parse.User.current();
      if (currentUser) {
        query.notEqualTo("objectId", currentUser.id);
      }

      if (filter) {
        Object.keys(filter).forEach((f) => {
          if (f === "manager") return; // skip pointer handling here
          if (typeof filter[f] === "string") {
            query.matches(f, filter[f], "i");
          } else {
            query.equalTo(f, filter[f]);
          }
        });

        // ✅ Manager filter (pointer)
        if (filter.manager) {
          const userQuery = new Parse.Query(Parse.User);
          userQuery.matches("objectId", filter.manager, "i");
          const managers = await userQuery.find({ useMasterKey: true });

          if (managers.length > 0) {
            const managerPointers = managers.map((m) => m.toPointer());
            query.containedIn("manager", managerPointers);
          } else {
            // Force no results if no match
            // query.equalTo("objectId", "__no_such_id__");
          }
        }
      }
    }
else if (resource === "User Status History") {
      const Resource = Parse.Object.extend("UserStatusHistory");
      query = new Parse.Query(Resource);
      query.include("actor");
      query.include("targetUser");
      query.descending("timestamp");

      // 🔍 Filtering by specific user’s history
      if (filter && filter["targetUser.objectId"]) {
        const targetPointer = {
          __type: "Pointer",
          className: "_User",
          objectId: filter["targetUser.objectId"],
        };
        query.equalTo("targetUser", targetPointer);
      }

      // 🔍 Optional date filter
      if (filter?.startDate || filter?.endDate) {
        const startDate = filter.startDate ? new Date(filter.startDate) : null;
        const endDate = filter.endDate
          ? (() => {
              const d = new Date(filter.endDate);
              d.setDate(d.getDate() + 1);
              return d;
            })()
          : null;
        if (startDate) query.greaterThanOrEqualTo("timestamp", startDate);
        if (endDate) query.lessThan("timestamp", endDate);
      }

      // Sort + Pagination
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      if (order === "DESC") query.descending(field || "timestamp");
      else query.ascending(field || "timestamp");
      query.skip((page - 1) * perPage);
      query.limit(perPage);

      count = await query.count({ useMasterKey: true });
      const results = await query.find({ useMasterKey: true });

      return {
        total: count,
        data: results.map((o) => ({
          id: o.id,
          newStatus: o.get("newStatus"),
          previousStatus: o.get("previousStatus"),
          timestamp: o.get("timestamp"),
          actor: o.get("actor")
            ? {
                id: o.get("actor").id,
                username: o.get("actor").get("username"),
              }
            : null,
          targetUser: o.get("targetUser")
            ? {
                id: o.get("targetUser").id,
                username: o.get("targetUser").get("username"),
              }
            : null,
        })),
      };
}
     else if (resource === "usersDropdown") {
      query = new Parse.Query(Parse.User);
      // console.log(Parse.User.current().attributes);
      query.descending("createdAt");
      query.include("role");

      filter &&
        Object.keys(filter).map((f) =>
          typeof filter[f] === "string"
            ? query.matches(f, filter[f], "i")
            : query.equalTo(f, filter[f])
        );

      if (filter.manager) {
        const userQuery = new Parse.Query(Parse.User);
        userQuery.matches("username", filter.manager, "i");
        const manager = await userQuery.find();

        filter &&
          manager.map((f) => {
            query.matches("manager", f.id, "i");
          });
      }
    } else if (resource === "usersHierarchy") {
      const userId = filter.userId;

      const buildUserFilterMatchStage = (filters) => {
        const match = {};

        if (filters.username?.trim()) {
          match.username = { $regex: filters.username.trim(), $options: "i" };
        }

        if (filters.publicEmail?.trim()) {
          match.publicEmail = {
            $regex: filters.publicEmail.trim(),
            $options: "i",
          };
        }

        if (filters.manager?.trim()) {
          match["managerData.username"] = {
            $regex: filters.manager.trim(),
            $options: "i",
          };
        }

        return Object.keys(match).length > 0 ? { $match: match } : null;
      };

      try {
        const skip = (page - 1) * perPage;
        const limit = perPage;

        const sortStage = {
          $sort: {
            [field]: order === "ASC" ? 1 : -1,
          },
        };

        const baseStages = [
          { $match: { _rperm: userId } },
          {
            $addFields: {
              firstRperm: { $arrayElemAt: ["$_rperm", 0] },
            },
          },
          {
            $match: {
              firstRperm: { $ne: userId },
            },
          },
          {
            $addFields: {
              roleIdStr: {
                $replaceOne: {
                  input: "$_p_role",
                  find: "_Role$",
                  replacement: "",
                },
              },
            },
          },
          {
            $lookup: {
              from: "_Role",
              localField: "roleIdStr",
              foreignField: "_id",
              as: "roleData",
              pipeline: [{ $project: { name: 1 } }],
            },
          },
          { $unwind: "$roleData" },
          {
            $addFields: {
              managerIdStr: {
                $replaceOne: {
                  input: "$_p_manager",
                  find: "_User$",
                  replacement: "",
                },
              },
            },
          },
          {
            $lookup: {
              from: "_User",
              localField: "managerIdStr",
              foreignField: "_id",
              as: "managerData",
              pipeline: [{ $project: { username: 1 } }],
            },
          },
          { $unwind: "$managerData" },
          {
            $match: {
              "roleData.name": { $ne: "Player" },
            },
          },
        ];

        // 👇 Inject filter stage if needed
        const filterMatchStage = buildUserFilterMatchStage(filter);
        if (filterMatchStage) baseStages.push(filterMatchStage);

        // -------------------- USER LIST --------------------
        const userList = [
          ...baseStages,
          {
            $project: {
              id: "$_id",
              username: 1,
              publicEmail: 1,
              phone: 1,
              balance: 1,
              _created_at: 1,
              roleName: "$roleData.name",
              managerName: "$managerData.username",
            },
          },
          sortStage,
          { $skip: skip },
          { $limit: limit },
        ];

        // -------------------- USER COUNT --------------------
        const userCount = [...baseStages, { $count: "totalCount" }];

        const [userData, userTotalCount] = await Promise.all([
          new Parse.Query("_User").aggregate(userList, { useMasterKey: true }),
          new Parse.Query("_User").aggregate(userCount, { useMasterKey: true }),
        ]);

        return {
          total: userTotalCount[0]?.totalCount || 0,
          data: userData,
        };
      } catch (error) {
        console.error("usersHierarchy error:", error);
        throw error;
      }
    } else if (resource === "roles") {
      query = new Parse.Query(Parse.Role);
      filter.notSelf &&
        query.notEqualTo(
          "objectId",
          (await Parse.User.current().get("role")).id
        );
    }else if (resource === "redeemRecords") {
      const Resource = Parse.Object.extend("Transactions");
      query = new Parse.Query(Resource);
      query.equalTo("txn_type", 1);

      const currentUser = Parse.User.current();
      if (currentUser) {
        query.notEqualTo("user", currentUser.toPointer());
      }

      if (filter.username) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === "string") {
            query.matches(f, filter[f], "i");
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      }

      if (filter.manager) {
        const userQuery = new Parse.Query(Parse.User);
        userQuery.matches("objectId", filter.manager, "i");
        const managers = await userQuery.find({ useMasterKey: true });

        if (managers.length > 0) {
          const managerPointers = managers.map((m) => m.toPointer());
          query.containedIn("manager", managerPointers);
        } else {
          // Force no results if no match
          //query.equalTo("objectId", "__no_such_id__");
        }
      }
    }
    else if (resource === "rechargeRecords") {
      const Resource = Parse.Object.extend("Transactions");
      query = new Parse.Query(Resource);
      query.equalTo("txn_type", 0); // ✅ recharge

      const currentUser = Parse.User.current();
      if (currentUser) {
        query.notEqualTo("user", currentUser.toPointer());
      }

      if (filter.username) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === "string") {
            query.matches(f, filter[f], "i");
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      } else if (filter.manager) {
        // 🔍 Manager filter
        const userQuery = new Parse.Query(Parse.User);
        userQuery.matches("objectId", filter.manager, "i");
        const managers = await userQuery.find({ useMasterKey: true });

        if (managers.length > 0) {
          const managerPointers = managers.map((m) => m.toPointer());
          query.containedIn("manager", managerPointers);
        } else {
          // Force no results if no match
          //query.equalTo("objectId", "__no_such_id__");
        }
      }
    }
     else if (resource === "gameRtp") {
      const Resource = Parse.Object.extend("GameRtp");
      query = new Parse.Query(Resource);
      filter &&
        Object.keys(filter).map((f) =>
          typeof filter[f] === "string"
            ? query.matches(f, filter[f], "i")
            : query.equalTo(f, filter[f])
        );
    } else if (resource === "dashboard") {
      const Resource = Parse.Object.extend("Transactions");
      query = new Parse.Query(Resource);

      query.descending("txn_datetime");
      query.limit(6);

      filter &&
        Object.keys(filter).map((f) =>
          typeof filter[f] === "string"
            ? query.matches(f, filter[f], "i")
            : query.equalTo(f, filter[f])
        );

      try {
        const results =
          resource === "users" ? await query.find() : await query.find();

        return {
          total: count,
          data: results?.map((o) => ({ id: o.id, ...o.attributes })),
        };
      } catch (error) {
        throw error;
      }
    } else if (resource === "gameList") {
      const Resource = Parse.Object.extend("GameCatalogue");
      query = new Parse.Query(Resource);

      filter &&
        Object.keys(filter).map((f) =>
          typeof filter[f] === "string"
            ? query.matches(f, filter[f], "i")
            : query.equalTo(f, filter[f])
        );

      try {
        const results = await query.find();
        return {
          total: count,
          data: results?.map((o) => ({ id: o.id, ...o.attributes })),
        };
      } catch (error) {
        throw error;
      }
    } else if (resource === "winingData") {
      const skip = (page - 1) * perPage;
      const limit = perPage;

      const sortField = field || "_created_at";
      const sortOrder = order === "ASC" ? 1 : -1;

      const getSortStage = (field, order) => {
        const fieldMap = {
          betAmount: "betAmount",
          winloseAmount: "winloseAmount",
          _created_at: "_created_at",
        };
        return {
          $sort: {
            [fieldMap[field] || "_created_at"]: order,
          },
        };
      };

      // -------------------- VEGAS GAME PIPELINE --------------------
      const tadaPipeline = [];
      tadaPipeline.push(
        {
          $addFields: {
            userIdStr: {
              $replaceOne: {
                input: "$_p_userId",
                find: "_User$",
                replacement: "",
              },
            },
            gameIdStr: {
              $replaceOne: {
                input: "$_p_gameId",
                find: "GameCatalogue$",
                replacement: "",
              },
            },
          },
        },
        {
          $match: {
            userIdStr: filter.userid,
          },
        },
        {
          $addFields: {
            _id: {
              $concat: ["VegasGameHistory$", { $toString: "$_id" }],
            },
          },
        },

        {
          $lookup: {
            from: "VegasGameSpinHistory",
            localField: "_id",
            foreignField: "_p_gameSessionId",
            as: "spinInfo",
          },
        },
        { $unwind: "$spinInfo" },

        {
          $lookup: {
            from: "GameCatalogue",
            localField: "gameIdStr",
            foreignField: "_id",
            as: "gameInfo",
          },
        },
        { $unwind: "$gameInfo" },

        {
          $project: {
            id: "$userIdStr",
            userIdStr: 1,
            gameName: "$gameInfo.name",
            beforeBalance: "$spinInfo.beforeBalance",
            afterBalance: "$spinInfo.afterBalance",
            winloseAmount: {
              $cond: [
                { $eq: ["$spinInfo.txn_type", 0] },
                "$spinInfo.amount",
                0,
              ],
            },
            betAmount: {
              $cond: [
                { $eq: ["$spinInfo.txn_type", 1] },
                "$spinInfo.amount",
                0,
              ],
            },
            type: { $literal: "vegas" },
            _created_at: 1,
          },
        },

        getSortStage(sortField, sortOrder),
        { $skip: skip },
        { $limit: limit }
      );

      // -------------------- VEGAS COUNT --------------------
      const countTadaPipeline = [];
      countTadaPipeline.push(
        {
          $addFields: {
            userIdStr: {
              $replaceOne: {
                input: "$_p_userId",
                find: "_User$",
                replacement: "",
              },
            },
            gameIdStr: {
              $replaceOne: {
                input: "$_p_gameId",
                find: "GameCatalogue$",
                replacement: "",
              },
            },
            gameSessionIdStr: {
              $concat: ["VegasGameHistory$", { $toString: "$_id" }],
            },
          },
        },
        {
          $match: {
            userIdStr: filter.userid,
          },
        },
        {
          $lookup: {
            from: "VegasGameSpinHistory",
            localField: "gameSessionIdStr",
            foreignField: "_p_gameSessionId",
            as: "spinInfo",
          },
        },
        { $unwind: "$spinInfo" },
        {
          $lookup: {
            from: "GameCatalogue",
            localField: "gameIdStr",
            foreignField: "_id",
            as: "gameInfo",
          },
        },
        { $unwind: "$gameInfo" },
        {
          $project: {
            id: "$userIdStr",
            gameName: "$gameInfo.name",
            beforeBalance: "$spinInfo.beforeBalance",
            afterBalance: "$spinInfo.afterBalance",
            winloseAmount: {
              $cond: [
                { $eq: ["$spinInfo.txn_type", 0] },
                "$spinInfo.amount",
                0,
              ],
            },
            betAmount: {
              $cond: [
                { $eq: ["$spinInfo.txn_type", 1] },
                "$spinInfo.amount",
                0,
              ],
            },
            type: { $literal: "vegas" },
            _created_at: 1,
          },
        },
        {
          $count: "totalCount",
        }
      );

      try {
        const resultTadaGame = await new Parse.Query(
          "VegasGameHistory"
        ).aggregate(tadaPipeline, { useMasterKey: true });

        const countTadaResult = await new Parse.Query(
          "VegasGameHistory"
        ).aggregate(countTadaPipeline, { useMasterKey: true });

        const totalTadaCount = countTadaResult[0]?.totalCount || 0;

        return {
          total: totalTadaCount,
          data: resultTadaGame,
        };
      } catch (error) {
        throw error;
      }
    }else if (resource === "gameCoins") {
      let user = Parse.User.current();
      user = await user.fetch();

      // ---------- helpers ----------
      const dateMatchStage = (startDate, endDate) => {
        const match = {};
        if (startDate) match.$gte = new Date(startDate);
        if (endDate) {
          const end = new Date(endDate);
          end.setDate(end.getDate() + 1);
          match.$lt = end;
        }
        return Object.keys(match).length > 0
          ? { $match: { gamePlayedDate: match } }
          : null;
      };

      const variantMatchStage = (variant) =>
        variant && variant !== "all" ? { $match: { type: variant } } : null;

      const gameNameMatchStage = (gameName) =>
        gameName && gameName.trim() !== ""
          ? { $match: { gameName: { $regex: gameName, $options: "i" } } }
          : null;

      try {
        const skip = (page - 1) * perPage;
        const limit = perPage;

        const sortStage = {
          $sort: {
            [field]: order === "ASC" ? 1 : -1,
          },
        };

        const includeTestingUsers = filter.includeTestingUsers === true;

        // ---------- common pre-filter stages (no lookups) ----------
        const baseMatchStages = [];
        const gameDateMatch = dateMatchStage(filter.startDate, filter.endDate);
        const gameVariantMatch = variantMatchStage(filter.gameVariant);
        const gameNameMatch = gameNameMatchStage(filter.gameName);

        if (gameDateMatch) baseMatchStages.push(gameDateMatch);
        if (gameVariantMatch) baseMatchStages.push(gameVariantMatch);
        if (gameNameMatch) baseMatchStages.push(gameNameMatch);

        // Scope to current user via ACL (keeps your original behavior)
        baseMatchStages.push({ $match: { _rperm: user.id } });

        // if manager is provided, also filter by manager
        if (filter.manager) {
          baseMatchStages.push({ $match: { _rperm: filter.manager } });
        }

        // Only player data (use denormalized roleName on GameCoins)
        //baseMatchStages.push({ $match: { role: "Player" } });

        // Exclude testing users unless explicitly included
        if (!includeTestingUsers) {
          // isTestingUserData is set to true for testing users; absence/false are treated as non-testing
          baseMatchStages.push({ $match: { isTestingUserData: { $ne: true } } });
        }

        // ---------- data pipeline ----------
        const gameCoinsList = [
          ...baseMatchStages,
          {
            $group: {
              _id: "$gameId",
              id: { $first: "$gameId" },
              totalCount: { $sum: 1 },
              totalBetAmount: { $sum: "$totalBetAmount" },
              totalWinAmount: { $sum: "$totalWinAmount" },
              gameName: { $first: "$gameName" },
              type: { $first: "$type" },
            },
          },
          {
            $project: {
              id: "$_id",
              type: 1,
              gameName: 1,
              totalBetAmount: 1,
              totalWinAmount: 1,
              rtp: {
                $cond: [
                  { $eq: ["$totalBetAmount", 0] },
                  0,
                  {
                    $multiply: [
                      { $divide: ["$totalWinAmount", "$totalBetAmount"] },
                      100,
                    ],
                  },
                ],
              },
              netAmount: {
                $subtract: ["$totalBetAmount", "$totalWinAmount"],
              },
            },
          },
          sortStage,
          { $skip: skip },
          { $limit: limit },
        ];

        // ---------- count pipeline ----------
        const gameCoinsListCount = [
          ...baseMatchStages,
          { $group: { _id: "$gameId" } },
          { $count: "totalCount" },
        ];

        const [data, countRes] = await Promise.all([
          new Parse.Query("GameCoins").aggregate(gameCoinsList, { useMasterKey: true }),
          new Parse.Query("GameCoins").aggregate(gameCoinsListCount, { useMasterKey: true }),
        ]);

        return {
          total: countRes[0]?.totalCount || 0,
          data,
        };
      } catch (error) {
        throw error;
      }
    }
    
    else if (resource === "userCoin") {
      const { gameType, username, startDate, endDate, gameId, manager, includeTestingUsers } = filter || {};
      const sortField = field || "createdAt";
      const sortOrder = order || "DESC";

      try {
        const response = await Parse.Cloud.run("getUserCoin", {
          page,
          perPage,
          gameType,
          username,
          startDate,
          endDate,
          gameId,
          manager,
          includeTestingUsers: includeTestingUsers || false,
          sortField,
          sortOrder,
        });

        return { total: response.total, data: response.data };
      } catch (error) {
        throw new Error(`Failed to fetch userCoin data: ${error.message}`);
      }
    }
    
    
    
    
    
    else if (resource === "userCoinAll") {
      const user = await Parse.User.current().fetch();
      const skip = (page - 1) * perPage;
      const limit = perPage;

      const { gameName, startDate, endDate, username } = filter || {};
      const sortField = field || "createdAt";
      const sortOrder = order === "ASC" ? "ascending" : "descending";

      try {
        let userIdList = null;
        let gameIdList = null;
        if (username) {
          const userQuery = new Parse.Query("User");
          userQuery.matches("username", new RegExp(username, "i")); // case-insensitive
          const matchedUsers = await userQuery.find({ useMasterKey: true });

          if (!matchedUsers.length) {
            return { total: 0, data: [] };
          }

          userIdList = matchedUsers.map((u) => u.id);
        }

        if (gameName) {
          const userQuery = new Parse.Query("GameCatalogue");
          userQuery.matches("name", new RegExp(gameName, "i")); // case-insensitive
          const matchedUsers = await userQuery.find({ useMasterKey: true });

          if (!matchedUsers.length) {
            return { total: 0, data: [] };
          }

          gameIdList = matchedUsers.map((u) => u.id);
        }
        // --- VEGAS GAME SPINS ---
        const vegasQuery = new Parse.Query("VegasGameSpinHistory");
        vegasQuery.limit(1000000);
        vegasQuery[sortOrder](sortField);
        vegasQuery.include("gameSessionId");

        if (startDate) vegasQuery.greaterThanOrEqualTo("createdAt", new Date(startDate));
        if (endDate) {
          const d = new Date(endDate);
          d.setDate(d.getDate() + 1);
          vegasQuery.lessThan("createdAt", d);
        }

        if (userIdList) vegasQuery.containedIn("userId", userIdList);
        if (gameIdList) vegasQuery.containedIn("gameId", gameIdList);

        const vegasSpins = await vegasQuery.find();
        const vegasSpinMap = new Map();

        for (const spin of vegasSpins) {
          const spinID = spin.get("spinID");
          const session = spin.get("gameSessionId");

          if (!spinID || !session || !session.id) continue;

          const key = `${spinID}-${session.id}`; // Grouping key

          const createdAt = spin.get("createdAt");
          const amount = spin.get("amount") || 0;
          const txnType = spin.get("txn_type");

          if (!vegasSpinMap.has(key)) {
            vegasSpinMap.set(key, {
              id: spin.id,
              userId: spin.get("userId"),
              gameId: spin.get("gameId"),
              gameType: "VE",
              betAmount: txnType === 1 ? amount : 0,
              winloseAmount: txnType === 0 ? amount : 0,
              beforeBalance: spin.get("beforeBalance"),
              afterBalance: spin.get("afterBalance"),
              createdAt: createdAt,
              tTime: spin.get("tTime"),
              spinID,
              gameSessionId: session.id,
            });
          } else {
            const entry = vegasSpinMap.get(key);

            // Accumulate amounts
            if (txnType === 1) {
              entry.betAmount += amount;
            } else if (txnType === 0) {
              entry.winloseAmount += amount;
            }

            // For latest data
            if (createdAt > entry.createdAt) {
              entry.createdAt = createdAt;
              entry.afterBalance = spin.get("afterBalance");
              entry.tTime = spin.get("tTime");
            }

            // For earliest data
            if (createdAt < entry.createdAt) {
              entry.beforeBalance = spin.get("beforeBalance");
            }
          }
        }

        const enrichedVegas = Array.from(vegasSpinMap.values());

        // --- TADA GAME SPINS ---
        const tadaQuery = new Parse.Query("TadaGameHistory");
        tadaQuery.limit(1000000);
        tadaQuery[sortOrder]("createdAt");
        tadaQuery.include("userId");

        if (startDate) tadaQuery.greaterThanOrEqualTo("createdAt", new Date(startDate));
        if (endDate) {
          const d = new Date(endDate);
          d.setDate(d.getDate() + 1);
          tadaQuery.lessThan("createdAt", d);
        }

        if (userIdList) tadaQuery.containedIn("userId", userIdList);
        if (gameIdList) tadaQuery.containedIn("gameId", gameIdList);
        const tadaSpins = await tadaQuery.find();
        const enrichedTada = [];

        const tadaGameMap = {
          265: "Crazy 777",
          266: "Bone Fortune",
          267: "Money Coming",
          270: "Golden Joker",
          275: "Charge Buffalo",
          277: "Golden Bank",
          279: "Magic Lamp",
          281: "Alibaba",
          282: "Gold Rush",
          284: "Wild Racer",
          285: "Shanghai Beauty",
          296: "Mega Fishing",
          306: "Dino shooter",
          310: "FaFaFa",
          318: "Party Night",
          322: "Fortune Pig",
          383: "Mines",
          390: "Limbo",
          395: "Mecha Jaeger",
          411: "Plinko",
          413: "ELF Bingo",
          432: "Crash",
          434: "Fortune Zombie",
          450: "Zeus",
          452: "Potion Wizard",
          454: "3 Coin Treasures",
          456: "The Pig House",
          465: "3 Lucky Piggy",
          481: "Devil Fire 2",
          482: "Ascent Charge Buffalo",
          493: "Fortune Gems Scratch",
          510: "Money Pot",
          556: "Lightning Fishhooter",
          566: "Jackpot Joker",
          568: "3 Rich Piggies",
          528: "Ocean Hunter",
          530: "Fortune Zombie 2",
          527: "Ocean King Jackpot",
          647: "Crazy 777 2",
          320: "Pirate Queen 2",
          278: "Fortune Gems 3",
          617: "Rapid Gems 777",
          575: "Crystal 777 Deluxe",
          614: "3 Lucky Lion",
          615: "3 Coin Wild Horse",
          565: "3 Coin Treasures 2",
          616: "Coin Nonet",
          1018: "Hexa Keno",
          567: "Sweet Magic",
          776: "Devil's Treasures Twins",
          764: "Coin Nonet Clover",
          650: "Clover Coins ",
          756: "3 Lion Awakening",
          784: "3 Lightning Blitz",
          648: "Circus Joker 4096",
          537: "Lucky Shooter"
        };

        for (const spin of tadaSpins) {
          const gameId = spin.get("gameId");
          enrichedTada.push({
            id: spin.id,
            userId: spin.get("userId"),
            gameId,
            gameName: tadaGameMap[gameId] || `Game ${gameId}`,
            gameType: "TD",
            betAmount: spin.get("betAmount"),
            winloseAmount: spin.get("winloseAmount"),
            beforeBalance: spin.get("balanceBeforeBet"),
            afterBalance: spin.get("balanceAfterBet"),
            createdAt: spin.get("createdAt"),
            tTime: spin.get("tTime")
          });
        }

        const combined = [...enrichedVegas, ...enrichedTada].sort((a, b) => {
          const aDate = new Date(a.createdAt).getTime();
          const bDate = new Date(b.createdAt).getTime();
          return sortOrder === "ascending" ? aDate - bDate : bDate - aDate;
        });

        const total = combined.length;
        const paginatedData = combined.slice(skip, skip + limit);
        return { total, data: paginatedData };
      } catch (error) {
        throw new Error(`Failed to fetch userCoinAll data: ${error.message}`);
      }
    }
    // NEW: allGamesUserDataV2 - Uses backend API for better performance
    else if (resource === "allGamesUserDataV2") {
      const { gameName, startDate, endDate, username } = filter || {};
      const sortField = field || "createdAt";
      const sortOrder = order || "DESC";

      try {
        // Convert dates to ISO format
        const startDateISO = startDate ? new Date(startDate).toISOString() : undefined;
        const endDateISO = endDate ? new Date(endDate + "T23:59:59").toISOString() : undefined;

        const result = await Parse.Cloud.run("getAllGamesUserData", {
          page,
          perPage,
          sortField,
          sortOrder,
          username: username || undefined,
          gameName: gameName || undefined,
          startDate: startDateISO,
          endDate: endDateISO,
        });

        return {
          total: result.total || 0,
          data: result.data || [],
        };
      } catch (error) {
        throw new Error(`Failed to fetch allGamesUserDataV2: ${error.message}`);
      }
    }
    else if (resource === "userCoinAllUser") {
      const user = await Parse.User.current().fetch();
      const skip = (page - 1) * perPage;
      const limit = perPage;

      const { gameName, startDate, endDate, username } = filter || {};
      const sortField = field || "createdAt";
      const sortOrder = order === "ASC" ? "ascending" : "descending";

      try {
        let userIdList = null;
        let gameIdList = null;
        if (username) {
          const userQuery = new Parse.Query("User");
          userQuery.matches("username", new RegExp(username, "i")); // case-insensitive
          const matchedUsers = await userQuery.find();

          if (!matchedUsers.length) {
            return { total: 0, data: [] };
          }

          userIdList = matchedUsers.map((u) => u.id);
        }

        if (gameName) {
          const userQuery = new Parse.Query("GameCatalogue");
          userQuery.matches("name", new RegExp(gameName, "i")); // case-insensitive
          const matchedUsers = await userQuery.find();

          if (!matchedUsers.length) {
            return { total: 0, data: [] };
          }

          gameIdList = matchedUsers.map((u) => u.id);
        }
        // --- VEGAS GAME SPINS ---
        const vegasQuery = new Parse.Query("VegasGameSpinHistory");
        vegasQuery.limit(1000000); // Fetch all matching records
        vegasQuery[sortOrder](sortField);
        vegasQuery.include("gameSessionId");

        if (startDate) vegasQuery.greaterThanOrEqualTo("createdAt", new Date(startDate));
        if (endDate) {
          const d = new Date(endDate);
          d.setDate(d.getDate() + 1);
          vegasQuery.lessThan("createdAt", d);
        }

        if (userIdList) vegasQuery.containedIn("userId", userIdList);
        if (gameIdList) vegasQuery.containedIn("gameId", gameIdList);

        const vegasSpins = await vegasQuery.find();
        const vegasSpinMap = new Map();

        for (const spin of vegasSpins) {
          const spinID = spin.get("spinID");
          const session = spin.get("gameSessionId");

          if (!spinID || !session || !session.id) continue;

          const key = `${spinID}-${session.id}`; // Grouping key

          const createdAt = spin.get("createdAt");
          const amount = spin.get("amount") || 0;
          const txnType = spin.get("txn_type");

          if (!vegasSpinMap.has(key)) {
            vegasSpinMap.set(key, {
              id: spin.id,
              userId: spin.get("userId"),
              gameId: spin.get("gameId"),
              gameType: "VE",
              betAmount: txnType === 1 ? amount : 0,
              winloseAmount: txnType === 0 ? amount : 0,
              beforeBalance: spin.get("beforeBalance"),
              afterBalance: spin.get("afterBalance"),
              createdAt: createdAt,
              tTime: spin.get("tTime"),
              spinID,
              gameSessionId: session.id,
            });
          } else {
            const entry = vegasSpinMap.get(key);

            // Accumulate amounts
            if (txnType === 1) {
              entry.betAmount += amount;
            } else if (txnType === 0) {
              entry.winloseAmount += amount;
            }

            // For latest data
            if (createdAt > entry.createdAt) {
              entry.createdAt = createdAt;
              entry.afterBalance = spin.get("afterBalance");
              entry.tTime = spin.get("tTime");
            }

            // For earliest data
            if (createdAt < entry.createdAt) {
              entry.beforeBalance = spin.get("beforeBalance");
            }
          }
        }

        const enrichedVegas = Array.from(vegasSpinMap.values());

        // --- TADA GAME SPINS ---
        const tadaQuery = new Parse.Query("TadaGameHistory");
        tadaQuery.limit(1000000); // Fetch all matching records
        tadaQuery[sortOrder]("createdAt");
        tadaQuery.include("userId");

        if (startDate) tadaQuery.greaterThanOrEqualTo("createdAt", new Date(startDate));
        if (endDate) {
          const d = new Date(endDate);
          d.setDate(d.getDate() + 1);
          tadaQuery.lessThan("createdAt", d);
        }

        if (userIdList) tadaQuery.containedIn("userId", userIdList);
        if (gameIdList) tadaQuery.containedIn("gameId", gameIdList);
        const tadaSpins = await tadaQuery.find();
        const enrichedTada = [];

        const tadaGameMap = {
          265: "Crazy 777",
          266: "Bone Fortune",
          267: "Money Coming",
          270: "Golden Joker",
          275: "Charge Buffalo",
          277: "Golden Bank",
          279: "Magic Lamp",
          281: "Alibaba",
          282: "Gold Rush",
          284: "Wild Racer",
          285: "Shanghai Beauty",
          296: "Mega Fishing",
          306: "Dino shooter",
          310: "FaFaFa",
          318: "Party Night",
          322: "Fortune Pig",
          383: "Mines",
          390: "Limbo",
          395: "Mecha Jaeger",
          411: "Plinko",
          413: "ELF Bingo",
          432: "Crash",
          434: "Fortune Zombie",
          450: "Zeus",
          452: "Potion Wizard",
          454: "3 Coin Treasures",
          456: "The Pig House",
          465: "3 Lucky Piggy",
          481: "Devil Fire 2",
          482: "Ascent Charge Buffalo",
          493: "Fortune Gems Scratch",
          510: "Money Pot",
          556: "Lightning Fishhooter",
          566: "Jackpot Joker",
          568: "3 Rich Piggies",
          528: "Ocean Hunter",
          530: "Fortune Zombie 2",
          527: "Ocean King Jackpot",
          647: "Crazy 777 2",
          320: "Pirate Queen 2",
          278: "Fortune Gems 3",
          617: "Rapid Gems 777",
          575: "Crystal 777 Deluxe",
          614: "3 Lucky Lion",
          615: "3 Coin Wild Horse",
          565: "3 Coin Treasures 2",
          616: "Coin Nonet",
          1018: "Hexa Keno",
          567: "Sweet Magic",
          776: "Devil's Treasures Twins",
          764: "Coin Nonet Clover",
          650: "Clover Coins ",
          756: "3 Lion Awakening",
          784: "3 Lightning Blitz",
          648: "Circus Joker 4096",
          537: "Lucky Shooter"
        };

        for (const spin of tadaSpins) {
          const gameId = spin.get("gameId");
          enrichedTada.push({
            id: spin.id,
            userId: spin.get("userId"),
            gameId,
            gameName: tadaGameMap[gameId] || `Game ${gameId}`,
            gameType: "TD",
            betAmount: spin.get("betAmount"),
            winloseAmount: spin.get("winloseAmount"),
            beforeBalance: spin.get("balanceBeforeBet"),
            afterBalance: spin.get("balanceAfterBet"),
            createdAt: spin.get("createdAt"),
            tTime: spin.get("tTime")
          });
        }

        const combined = [...enrichedVegas, ...enrichedTada].sort((a, b) => {
          const aDate = new Date(a.createdAt).getTime();
          const bDate = new Date(b.createdAt).getTime();
          return sortOrder === "ascending" ? aDate - bDate : bDate - aDate;
        });

        // Apply pagination to combined results
        const total = combined.length;
        const paginatedData = combined.slice(skip, skip + limit);

        return { total, data: paginatedData };
      } catch (error) {
        throw new Error(`Failed to fetch userCoinAll data: ${error.message}`);
      }
    }
    
    
    
     else if (resource === "dayWiseCoins") {
      let user = Parse.User.current();
      user = await user.fetch();

      const dateMatchStage = (startDate, endDate) => {
        const match = {};
        if (startDate) match.$gte = new Date(startDate);
        if (endDate) {
          const end = new Date(endDate);
          end.setDate(end.getDate() + 1);
          match.$lt = end;
        }
        return Object.keys(match).length > 0
          ? { $match: { gamePlayedDate: match } }
          : null;
      };

      const variantMatchStage = (variant) =>
        variant && variant !== "all" ? { $match: { type: variant } } : null;

      try {
        const skip = (page - 1) * perPage;
        const limit = perPage;

        const sortStage = {
          $sort: {
            [field]: order === "ASC" ? 1 : -1,
          },
        };

        // -------------------- COMMON FILTERS --------------------
        const matchStages = [];

        const gameDateMatch = dateMatchStage(filter.startDate, filter.endDate);
        const gameVariantMatch = variantMatchStage(filter.gameVariant);

        if (gameDateMatch) matchStages.push(gameDateMatch);
        if (gameVariantMatch) matchStages.push(gameVariantMatch);

        // always filter by current user
        matchStages.push({ $match: { _rperm: user.id } });

        // if manager is provided, also filter by manager
        if (filter.manager) {
          matchStages.push({ $match: { _rperm: filter.manager } });
        }


        // Optional: Exclude testing users
        if (!filter.includeTestingUsers) {
          matchStages.push({ $match: { isTestingUserData: { $ne: true } } });
        }

        // -------------------- DATA PIPELINE --------------------
        const gameCoinsList = [
          ...matchStages,
          {
            $addFields: {
              createdDateStr: {
                $dateToString: {
                  format: "%Y-%m-%d",
                  date: "$gamePlayedDate",
                },
              },
            },
          },
          {
            $group: {
              _id: "$createdDateStr",
              totalBetAmount: { $sum: "$totalBetAmount" },
              totalWinAmount: { $sum: "$totalWinAmount" },
              sessionCount: { $sum: "$sessionCount" },
            },
          },
          {
            $project: {
              id: "$_id",
              totalBetAmount: 1,
              totalWinAmount: 1,
              sessionCount: 1,
              rtp: {
                $cond: [
                  { $eq: ["$totalBetAmount", 0] },
                  0,
                  {
                    $multiply: [
                      { $divide: ["$totalWinAmount", "$totalBetAmount"] },
                      100,
                    ],
                  },
                ],
              },
              netAmount: {
                $subtract: ["$totalBetAmount", "$totalWinAmount"],
              },
            },
          },
          sortStage,
          { $skip: skip },
          { $limit: limit },
        ];

        // -------------------- COUNT PIPELINE --------------------
        const countPipeline = [
          ...matchStages,
          {
            $addFields: {
              createdDateStr: {
                $dateToString: {
                  format: "%Y-%m-%d",
                  date: "$gamePlayedDate",
                },
              },
            },
          },
          {
            $group: {
              _id: "$createdDateStr",
            },
          },
          { $count: "totalCount" },
        ];

        const [data, countRes] = await Promise.all([
      new Parse.Query("GameCoins").aggregate(gameCoinsList, { useMasterKey: true }),
      new Parse.Query("GameCoins").aggregate(countPipeline, { useMasterKey: true }),
        ]);

        return {
          total: countRes[0]?.totalCount || 0,
          data,
        };
      } catch (error) {
        console.error("❌ dayWiseCoins error:", error);
        throw error;
      }
}
else if (resource === "accounts") {
      let user = Parse.User.current();
      user = await user.fetch();
      const roleHierarchy = [
        "Super-User",
        "Game-Owner",
        "Master-Distributor",
        "Distributor",
        "Sub-Distributor",
        "Agent",
        "Player",
      ];
      const roleQuery = new Parse.Query(Parse.Role);
      roleQuery.equalTo("users", user);
      const currentRole = await roleQuery.first({ useMasterKey: true });

      let allowedRoleName = null;
      let allowedUsers = [];
      let totalCount = 0;

      if (currentRole) {
        const idx = roleHierarchy.indexOf(currentRole.get("name"));
        if (idx >= 0 && idx < roleHierarchy.length - 1) {
          allowedRoleName = roleHierarchy[idx + 1];
          allowedRoleName =
            filter.roleName && filter.roleName !== "All"
              ? filter.roleName
              : allowedRoleName;
          // 🔹 Fetch allowed users with pagination
          const roleObj = await new Parse.Query(Parse.Role)
            .equalTo("name", allowedRoleName)
            .first();

          if (filter.manager) {
            const userQuery = new Parse.Query(Parse.User);
            userQuery.equalTo("objectId", filter.manager);
            userQuery.include("manager");
          userQuery.include("role")
            userQuery.include("balance");
            const users = await userQuery.find();
            allowedUsers = users.map((u) => ({
              id: u.id,
              username: u.get("username"),
              roleName: u.get("role")?.get("name") || "Unknown",
            managerName: u.get("manager")?.get("username") ||  u.get("manager")?.get("name") || null ,
            balance: u.get("balance")
            }));
            totalCount = await userQuery.count();
        }
     else if (roleObj) {
            const relation = roleObj.relation("users");
            const q = relation.query();
            q.skip((page - 1) * perPage);
            q.limit(perPage);
            q.include("manager");
            q.include("role");
            q.include("balance");
            const users = await q.find();

            allowedUsers = users.map((u) => ({
              id: u.id,
              username: u.get("username"),
              roleName: u.get("role")?.get("name") || "Unknown",
              managerName: u.get("manager")?.get("username") || null,
          balance: u.get("balance")
            }));

            const relationCount = roleObj.relation("users");
            const qCount = relationCount.query();
            totalCount = await qCount.count();

          }
        }
      }

      const dateMatch = (startDate, endDate, fieldName) => {
        const match = {};
        if (startDate) match.$gte = new Date(startDate);
        if (endDate) {
          const end = new Date(endDate);
          end.setDate(end.getDate() + 1);
          match.$lt = end;
        }
        return Object.keys(match).length > 0 ? { [fieldName]: match } : null;
      };

      try {
    const gameDateMatch = dateMatch(filter.startDate, filter.endDate, "txn_datetime");

        const gcStart = filter.startDate ? new Date(filter.startDate) : null;
        const gcEnd = filter.endDate
          ? (() => {
              const d = new Date(filter.endDate);
              d.setDate(d.getDate() + 1);
              return d;
            })()
          : null;

        const results = [];

        for (const userInfo of allowedUsers) {
          const uid = userInfo.id;
          const txnPipeline = [
            ...(gameDateMatch ? [{ $match: gameDateMatch }] : []),
            { $match: { _rperm: uid } },
            {
              $group: {
                _id: null,
                totalRecharge: {
                  $sum: { $cond: [{ $eq: ["$txn_type", 0] }, "$amount", 0] },
                },
                totalRedeem: {
                  $sum: { $cond: [{ $eq: ["$txn_type", 1] }, "$amount", 0] },
                },
              },
            },
            {
              $addFields: {
                netAmount: { $subtract: ["$totalRecharge", "$totalRedeem"] },
                userId: uid,
              },
            },
          ];
      const txnData = await new Parse.Query("Transactions").aggregate(txnPipeline, { useMasterKey: true });
          const gcPipeline = [
            { $match: { _rperm: uid } },
            ...(gcStart || gcEnd
              ? [
                  {
                    $match: {
                      ...(gcStart ? { createdAt: { $gte: gcStart } } : {}),
                      ...(gcEnd ? { createdAt: { $lt: gcEnd } } : {}),
                    },
                  },
                ]
              : []),
            {
              $group: {
                _id: null,
                totalBetAmount: { $sum: { $ifNull: ["$totalBetAmount", 0] } },
                totalWinAmount: { $sum: { $ifNull: ["$totalWinAmount", 0] } },
                sessionCount: { $sum: { $ifNull: ["$sessionCount", 0] } },
              },
            },
            {
              $addFields: {
                userId: uid,
                rtp: {
                  $cond: [
                    { $eq: ["$totalBetAmount", 0] },
                    0,
                    {
                      $multiply: [
                        { $divide: ["$totalWinAmount", "$totalBetAmount"] },
                        100,
                      ],
                    },
                  ],
                },
            netGameAmount: { $subtract: ["$totalBetAmount", "$totalWinAmount"] },
              },
            },
          ];
      const gcData = await new Parse.Query("GameCoins").aggregate(gcPipeline, { useMasterKey: true });

          const txn = txnData[0] || {
            totalRecharge: 0,
            totalRedeem: 0,
            netAmount: 0,
          };

          const gc = gcData[0] || {
            totalBetAmount: 0,
            totalWinAmount: 0,
            sessionCount: 0,
            rtp: 0,
            netGameAmount: 0,
          };

          // 🔹 Calculate holding (rechargeRatio) safely in JS
          const holding =
            txn.totalRecharge === 0
              ? 0
              : (txn.netAmount / txn.totalRecharge) * 100;
          results.push({
            id: userInfo.id,
            ...txn,
            ...gc,
            username: userInfo.username,
            roleName: userInfo.roleName,
            managerName: userInfo.managerName,
        rechargeRatio:holding,
        balance: userInfo.balance
          });
          
        }

        // --- Count total allowed users for pagination UI ---
        //const totalCount = allowedUsers.length;
    console.log(totalCount,results,"results--totalCount")
        return {
          total: totalCount,
          data: results,
        };
      } catch (error) {
        throw error;
      }
}
     else if (resource === "agentAccount") {
      let user = Parse.User.current();
      user = await user.fetch();
      const userId = user.id;

      const dateMatchStage = (startDate, endDate, fieldName) => {
        const match = {};
        if (startDate) match.$gte = new Date(startDate);
        if (endDate) {
          const end = new Date(endDate);
          end.setDate(end.getDate() + 1); // exclusive end
          match.$lt = end;
        }
        return Object.keys(match).length > 0 ? { $match: { [fieldName]: match } } : null;
      };

      try {
        const skip = (page - 1) * perPage;
        const limit = perPage;
        const sortStage = { $sort: { [field]: order === "ASC" ? 1 : -1 } };
        const gameDateMatch = dateMatchStage(filter.startDate, filter.endDate, "txn_datetime");

        // For GameCoins date filter (uses system field _updated_at in Aggregation)
        const gcStart = filter.startDate ? new Date(filter.startDate) : null;
        const gcEnd = filter.endDate ? (() => { const d = new Date(filter.endDate); d.setDate(d.getDate() + 1); return d; })() : null;

        const baseUserLookupPipeline = [
          { $project: { _p_role: 1, username: 1, _p_manager: 1, objectId: "$_id" } },
          { $match: { objectId: { $ne: userId } } }
        ];

        const gameCoinsList = [];
        if (gameDateMatch) gameCoinsList.push(gameDateMatch);

        gameCoinsList.push(
          { $match: { _rperm: user.id } },
          { $addFields: { firstRperm: { $arrayElemAt: ["$_rperm", 0] } } },
          {
            $lookup: {
              from: "_User",
              localField: "firstRperm",
              foreignField: "_id",
              as: "userData",
              pipeline: baseUserLookupPipeline
            }
          },
          { $unwind: "$userData" },
          { $addFields: { roleIdStr: { $replaceOne: { input: "$userData._p_role", find: "_Role$", replacement: "" } } } },
          {
            $lookup: {
              from: "_Role",
              localField: "roleIdStr",
              foreignField: "_id",
              as: "roleData",
              pipeline: [{ $project: { name: 1 } }]
            }
          },
          { $unwind: "$roleData" },
          { $addFields: { managerIdStr: { $replaceOne: { input: "$userData._p_manager", find: "_User$", replacement: "" } } } },
          {
            $lookup: {
              from: "_User",
              localField: "managerIdStr",
              foreignField: "_id",
              as: "managerData",
              pipeline: [{ $project: { username: 1 } }]
            }
          },
          { $unwind: "$managerData" },
          { $match: { "roleData.name": { $ne: "Player" } } },
          {
            $group: {
              _id: "$userData._id",
              id: { $first: "$userData._id" },
              parentName: { $first: "$managerData.username" },
              userName: { $first: "$userData.username" },
              roleName: { $first: "$roleData.name" },
              totalRecharge: { $sum: { $cond: [{ $eq: ["$txn_type", 0] }, "$amount", 0] } },
              totalRedeem: { $sum: { $cond: [{ $eq: ["$txn_type", 1] }, "$amount", 0] } }
            }
          },
          { $addFields: { netAmount: { $subtract: ["$totalRecharge", "$totalRedeem"] } } },

          // ===== NEW: pull totals from GameCoins for each user id =====
          {
            $lookup: {
              from: "GameCoins",
              let: { uid: "$id" },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: [
                        { $in: ["$$uid", "$_rperm"] },
                        ...(gcStart ? [{ $gte: ["$_updated_at", gcStart] }] : []),
                        ...(gcEnd ? [{ $lt: ["$_updated_at", gcEnd] }] : []),
                      ]
                    }
                  }
                },
                {
                  $group: {
                    _id: null,
                    totalBetAmount: { $sum: { $ifNull: ["$totalBetAmount", 0] } },
                    totalWinAmount: { $sum: { $ifNull: ["$totalWinAmount", 0] } }
                  }
                }
              ],
              as: "coinsAgg"
            }
          },
          {
            $addFields: {
              totalBetAmount: { $ifNull: [{ $first: "$coinsAgg.totalBetAmount" }, 0] },
              totalWinAmount: { $ifNull: [{ $first: "$coinsAgg.totalWinAmount" }, 0] }
            }
          },
          { $project: { coinsAgg: 0 } },
          // ===== END NEW =====

          {
            $project: {
              id: 1,
              parentName: 1,
              userName: 1,
              roleName: 1,
              totalRecharge: 1,
              totalRedeem: 1,
              netAmount: 1,
              totalBetAmount: 1,   // include in output
              totalWinAmount: 1,   // include in output
              rechargeRatio: {
                $cond: [
                  { $eq: ["$totalRecharge", 0] },
                  0,
                  { $multiply: [{ $divide: ["$netAmount", "$totalRecharge"] }, 100] }
                ]
              }
            }
          },
          sortStage,
          { $skip: skip },
          { $limit: limit }
        );

        const gameCoinsListCount = [
          ...(gameDateMatch ? [gameDateMatch] : []),
          { $match: { _rperm: user.id } },
          { $addFields: { firstRperm: { $arrayElemAt: ["$_rperm", 0] } } },
          {
            $lookup: {
              from: "_User",
              localField: "firstRperm",
              foreignField: "_id",
              as: "userData",
              pipeline: baseUserLookupPipeline
            }
          },
          { $unwind: "$userData" },
          { $addFields: { roleIdStr: { $replaceOne: { input: "$userData._p_role", find: "_Role$", replacement: "" } } } },
          {
            $lookup: {
              from: "_Role",
              localField: "roleIdStr",
              foreignField: "_id",
              as: "roleData",
              pipeline: [{ $project: { name: 1 } }]
            }
          },
          { $unwind: "$roleData" },
          { $match: { "roleData.name": { $ne: "Player" } } },
          { $group: { _id: "$userData._id" } },
          { $count: "totalCount" }
        ];

        const [transaction, countRes] = await Promise.all([
          new Parse.Query("Transactions").aggregate(gameCoinsList, { useMasterKey: true }),
          new Parse.Query("Transactions").aggregate(gameCoinsListCount, { useMasterKey: true })
        ]);
        console.log(transaction,countRes[0]?.totalCount,"countRes[0]?.totalCountcountRes[0]?.totalCount")
        return {
          total: countRes[0]?.totalCount || 0,
          data: transaction
        };
      } catch (error) {
        throw error;
      }
    }
    else if (resource === "wheelSpinHistory") {
      try {
        console.log("wheelSpinHistory filters:", filter);
        console.log("wheelSpinHistory sort:", params.sort);

        const { field, order } = params.sort;

        const response = await Parse.Cloud.run("getLuckyWheelTransactionHistory", {
            page: page,
            limit: perPage,
            search: filter.playerName || "",
            sortField: field || "transactionDate",
          sortOrder: order || "DESC"
        });

        if (response && response.success) {
          const formattedData = response.transactions.map(txn => ({
            id: txn.transactionId,
            ...txn,
            // Format dates for display
            transactionDate: txn.transactionDate?.iso ? new Date(txn.transactionDate.iso) : txn.transactionDate,
            createdAt: txn.createdAt?.iso ? new Date(txn.createdAt.iso) : txn.createdAt,
            // Format reward type
            rewardTypeText: txn.rewardType === 1 ? 'Crown' : txn.rewardType === 2 ? 'Gems' : 'Good Luck'
          }));

          return {
            total: response.pagination.totalCount,
            data: formattedData
          };
        } else {
          throw new Error(response?.error || "Failed to fetch wheel spin history");
        }
      } catch (error) {
        console.error("Error in wheelSpinHistory provider:", error);
        throw error;
      }
    } else if (resource === "hourlyBonusHistory") {
      try {
        console.log("hourlyBonusHistory filters:", filter);
        console.log("hourlyBonusHistory sort:", params.sort);

        const { field, order } = params.sort;

        const response = await Parse.Cloud.run("getHourlyBonusTransactionHistory", {
            page: page,
            limit: perPage,
            search: filter.playerName || "",
            sortField: field || "transactionDate",
          sortOrder: order || "DESC"
        });

        if (response && response.success) {
          const formattedData = response.transactions.map(txn => ({
            id: txn.transactionId,
            ...txn,
            // Format dates for display
            transactionDate: txn.transactionDate?.iso ? new Date(txn.transactionDate.iso) : txn.transactionDate,
            createdAt: txn.createdAt?.iso ? new Date(txn.createdAt.iso) : txn.createdAt,
            // Format reward type
            rewardTypeText: txn.rewardType === 1 ? 'Crown' : txn.rewardType === 2 ? 'Gems' : 'Good Luck'
          }));

          return {
            total: response.pagination.totalCount,
            data: formattedData
          };
        } else {
          throw new Error(response?.error || "Failed to fetch hourly bonus history");
        }
      } catch (error) {
        console.error("Error in hourlyBonusHistory provider:", error);
        throw error;
      }
    } else if (resource === "agentAccountReport") {
      try {
        console.log("agentAccountReport filters:", filter);
        console.log("agentAccountReport sort:", params.sort);

        const { field, order } = params.sort;

        const response = await Parse.Cloud.run("getAgentAccountReport", {
          page: page,
          limit: perPage,
          search: filter.agentName || "",
          dateFrom: filter.dateFrom || null,
          dateTo: filter.dateTo || null,
          holdingMin: filter.holdingMin || null,
          holdingMax: filter.holdingMax || null,
          parentId: filter.parentId || null,
          sortBy: field || "agentName",
          sortOrder: order || "ASC",
        });

        console.log("agentAccountReport response:", response);

        if (response && response.success) {
          const formattedData = response.data.map((row) => ({
            id: row.agentId || row.srNo,
            ...row,
            // Format dates for display
            agentFirstRechargeDate: row.agentFirstRechargeDate?.iso
              ? new Date(row.agentFirstRechargeDate.iso)
              : row.agentFirstRechargeDate,
            agentLastRechargeDate: row.agentLastRechargeDate?.iso
              ? new Date(row.agentLastRechargeDate.iso)
              : row.agentLastRechargeDate,
            agentLastRedeemDate: row.agentLastRedeemDate?.iso
              ? new Date(row.agentLastRedeemDate.iso)
              : row.agentLastRedeemDate,
            subDistributorFirstRechargeDate: row.subDistributorFirstRechargeDate
              ?.iso
              ? new Date(row.subDistributorFirstRechargeDate.iso)
              : row.subDistributorFirstRechargeDate,
            distributorFirstRechargeDate: row.distributorFirstRechargeDate?.iso
              ? new Date(row.distributorFirstRechargeDate.iso)
              : row.distributorFirstRechargeDate,
            masterDistributorFirstRechargeDate: row
              .masterDistributorFirstRechargeDate?.iso
              ? new Date(row.masterDistributorFirstRechargeDate.iso)
              : row.masterDistributorFirstRechargeDate,
            gameOwnerFirstRechargeDate: row.gameOwnerFirstRechargeDate?.iso
              ? new Date(row.gameOwnerFirstRechargeDate.iso)
              : row.gameOwnerFirstRechargeDate,
          }));

          return {
            total: response.total,
            data: formattedData,
          };
        } else {
          throw new Error(response?.message || "Failed to fetch agent report");
        }
      } catch (error) {
        console.error("Error in agentAccountReport provider:", error);
        throw error;
      }
    } else if (resource === "tickets") {
      try {
        const Resource = Parse.Object.extend("Ticket");
        query = new Parse.Query(Resource);
        
        // Get current user and role
        const currentUser = Parse.User.current();
        const role = localStorage.getItem("role");
        
        // Only filter by userId if not Super-User
        if (currentUser && role !== "Super-User") {
          query.equalTo("userId", currentUser.id);
        }

        // Apply filters
        if (filter) {
          if (filter.status) {
            query.equalTo("status", filter.status);
          }
          if (filter.category) {
            query.equalTo("category", filter.category);
          }
          if (filter.role) {
            query.equalTo("role", filter.role);
          }
          // Search by username or ticketId
          if (filter.searchBy) {
            const searchTerm = filter.searchBy.trim();
            const usernameQuery = new Parse.Query(Resource);
            usernameQuery.matches("username", searchTerm, "i");
            
            const ticketIdQuery = new Parse.Query(Resource);
            ticketIdQuery.matches("objectId", searchTerm, "i");
            
            query = Parse.Query.or(usernameQuery, ticketIdQuery);
            
            // Re-apply other filters
            if (filter.status) {
              query.equalTo("status", filter.status);
            }
            if (filter.category) {
              query.equalTo("category", filter.category);
            }
            if (currentUser && role !== "Super-User") {
              query.equalTo("userId", currentUser.id);
            }
          }
        }
      } catch (error) {
        console.error("Error in tickets provider:", error);
        throw error;
      }
    } else {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      filter &&
        Object.keys(filter).map((f) =>
          typeof filter[f] === "string"
            ? query.matches(f, filter[f], "i")
            : query.equalTo(f, filter[f])
        );
    }

    count = await query.count();

    query.limit(perPage);
    query.skip((page - 1) * perPage);
    if (order === "DESC") query.descending(field==="id"?"objectId":field);
    else if (order === "ASC") query.ascending(field==="id"?"objectId":field);
    try {
      // const results = resource === (await query.find());
      const results = await query.find();

      // Clear previous users and store only current page users
      currentPageUsers.clear();

      return {
        total: count,
        data: results?.map((o) => {
          // Add role name and precedence only if resource is "users"
          if (resource === "users") {
            const role = o.get("role");
            const roleName = role ? role.get("name") : null;
            const rolePrecedence = role ? role.get("precedence") : null;
            const userId = o.id;
            currentPageUsers.add(userId);

            // Ensure that user data is updated in the cache
            const userData = {
              id: userId,
              roleName,
              rolePrecedence,
              isTesting: o.get("isTesting") === true,
              canMarkTestUsers: o.get("canMarkTestUsers") === true,
              ...o.attributes,
            };

            return userData;
          } else if (resource === "usersDropdown") {
            const role = o.get("role");
            const roleName = role ? role.get("name") : null;
            const rolePrecedence = role ? role.get("precedence") : null;
            const userId = o.id;
            currentPageUsers.add(userId);

            // Ensure that user data is updated in the cache
            const userData = {
              id: userId,
              roleName,
              rolePrecedence,
              ...o.attributes,
            };

            return userData;
          } else if (resource === "usersHierarchy") {
            const role = o.get("role");
            const roleName = role ? role.get("name") : null;
            const rolePrecedence = role ? role.get("precedence") : null;
            const userId = o.id;
            currentPageUsers.add(userId);

            // Ensure that user data is updated in the cache
            const userData = {
              id: userId,
              roleName,
              rolePrecedence,
              ...o.attributes,
            };

            return userData;
          } else {
            return { id: o.id, ...o.attributes };
          }
        }),
      };
    } catch (error) {
      throw error;
    }
  },
  getMany: async (resource, params) => {
    var query = null;
    var results = null;

    if (resource === "users") {
      results = params.ids
        .map((obj) => {
          if (typeof obj === "string")
            return new Parse.Query(Parse.User).get(obj, { useMasterKey: true });
        })
        .filter((n) => n);
    } else if (resource === "roles") {
      results = params.ids
        .map((obj) => {
          if (typeof obj === "string")
            return new Parse.Query(Parse.Role).get(obj, { useMasterKey: true });
        })
        .filter((n) => n);
    } else if (resource === "GameCatalogue") {
      results = params.ids
        .map((obj) => {
          if (typeof obj === "string")
            return new Parse.Query("VegasBuilding").get(obj, {
              useMasterKey: true,
            });
        })
        .filter((n) => n);
    } else if (resource === "GameRtp") {
      results = params.ids
        .map((obj) => {
          if (typeof obj === "string")
            return new Parse.Query("GameCatalogue").get(obj, {
              useMasterKey: true,
            });
        })
        .filter((n) => n);
    } else {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      results = params.ids.map((id) => new Parse.Query(Resource).get(id));
    }
    try {
      const data = await Promise.all(results);
      // const data = results;
      return {
        // total: data.length,
        data: data.map((o) => ({ id: o.id, ...o.attributes })),
      };
    } catch (error) {
      throw error;
    }
  },
  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const Resource = Parse.Object.extend(resource);
    var query = null;

    if (resource === "users") {
      query = new Parse.Query(Parse.User);
    } else {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
    }

    query.equalTo(params.target, params.id);
    const count = await query.count();
    if (perPage) query.limit(perPage);
    if (page) query.skip((page - 1) * perPage);
    if (order === "DESC") query.descending(field);
    else if (order === "ASC") query.ascending(field);

    try {
      const results = await query.find();
      return {
        total: count,
        data: results?.map((o) => ({ id: o.id, ...o.attributes })),
      };
    } catch (error) {
      return error;
    }
  },
  update: async (resource, params) => {
    let query = null;
    let obj = null;
    let r = null;

    // Fields to exclude from the update process
    const excludeFields = [
      "id",
      "sessionToken",
      "createdAt",
      "updatedAt",
      "ACL",
      "confirmPassword",
    ];

    // Filter params.data to include only updatable fields
    const data = Object.keys(params.data)
      .filter((key) => !excludeFields.includes(key))
      .reduce((result, key) => {
        result[key] = params.data[key];
        return result;
      }, {});

    try {
      if (resource === "users") {
        // Query the Parse.User class correctly
        const query = new Parse.Query(Parse.User);

        // Fetch the user with master key
        const user = await query.get(params.data.id, { useMasterKey: true });

        // // Update fields from request data
        // for (const key in data) {
        //   user.set(key, data[key]);
        // }

        // If password is included, update it
        if (params.data.password) {
          user.setPassword(params.data.password);
        }

        // Save the updated user object using master key
        const result = await user.save(null, { useMasterKey: true });

        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "usersSwitch") {
        // Fetch the existing object from Parse
        const ParseUser = Parse.Object.extend(Parse.User);
        const query = new Parse.Query(ParseUser);
        const userActiveObject = await query.get(params.id);

        userActiveObject.set("isActive", params?.data?.isActive);
        userActiveObject.set("suspendedDate", params?.data?.suspendedDate);

        try {
          const result = await userActiveObject.save();

          // Emit WebSocket event
          try {
            sendWebSocketMessage("message",JSON.stringify({
                eventName: "user_status",
                isActive: params?.data?.isActive,
                userId: params.id,
            }));
          } catch (socketError) {
            console.error("WebSocket emit failed:", socketError);
            throw new Error("Update failed due to WebSocket error.");
          }
          return { data: { id: result.id, ...result.attributes } };
        } catch (err) {
          console.error("User update process failed:", err);
          throw new Error(
            err.message || "User update failed. Please try again."
          );
        }
      }
      if (resource === "gameRtp") {
        const rawData = params.data;

        // Transform reelName to the desired format
        if (
          typeof rawData.reelName === "object" &&
          !Array.isArray(rawData.reelName)
        ) {
          // Create the structure as an array of objects
          rawData.reelName = [
            { reelOne: rawData.reelName.reelOne },
            { reelTwo: rawData.reelName.reelTwo },
            { reelThree: rawData.reelName.reelThree },
            { reelFour: rawData.reelName.reelFour },
            { reelFive: rawData.reelName.reelFive },
          ];
        }

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.reelName) || rawData.reelName.length === 0) {
          throw new Error("Invalid or missing reelName array.");
        }

        // Query for the existing gameRtp data by gameId
        const gameRtpQuery = new Parse.Query("GameRtp");
        const queryOutput = await gameRtpQuery.get(rawData.id);

        // Update the object fields (set the values you want to update)
        // You can update multiple fields this way
        if (rawData.gameName) {
          queryOutput.set("gameName", rawData.gameName);
        }
        if (typeof rawData.gameRtp === "number") {
          queryOutput.set("gameRtp", rawData.gameRtp);
        }
        if (Array.isArray(rawData.reelName)) {
          queryOutput.set("reelName", rawData.reelName);
        }

        // Save the updated gameRtp object
        const result = await queryOutput.save(null, { useMasterKey: true });

        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "firelinkGameRtp") {
        const rawData = params.data;

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.firelinkReelName) || rawData.firelinkReelName.length === 0) {
          throw new Error("Invalid or missing firelinkReelName array.");
        }

        // Query for the existing GameRtp data by id (same class as regular games)
        const gameRtpQuery = new Parse.Query("GameRtp");
        const queryOutput = await gameRtpQuery.get(rawData.id);

        // Update the object fields
        if (rawData.gameName) {
          queryOutput.set("gameName", rawData.gameName);
        }
        if (typeof rawData.gameRtp === "number") {
          queryOutput.set("gameRtp", rawData.gameRtp);
        }
        if (Array.isArray(rawData.firelinkReelName)) {
          queryOutput.set("firelinkReelName", rawData.firelinkReelName); // Different column for Firelink
        }

        // Reference gameId from GameCatalogue if provided and it's a new gameId
        if (rawData.gameId && typeof rawData.gameId === 'string') {
          const GameCatalogue = Parse.Object.extend("GameCatalogue");
          const gameCatalogueQuery = new Parse.Query(GameCatalogue);

          try {
            const gameCatalogueObject = await gameCatalogueQuery.get(rawData.gameId);
            if (!gameCatalogueObject) {
              throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}`);
            }
            queryOutput.set("gameId", gameCatalogueObject);
          } catch (error) {
            console.error("GameCatalogue lookup error in update:", error);
            throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}. Error: ${error.message}`);
          }
        }
        // If gameId is already a pointer object or not provided, skip validation

        // Save the updated GameRtp object with firelinkReelName column
        const result = await queryOutput.save(null, { useMasterKey: true });

        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "kenoGameRtp") {
        const rawData = params.data;

        // Validate required fields
        if (!rawData.gameName || typeof rawData.gameName !== "string") {
          throw new Error("Invalid or missing gameName.");
        }
        if (!rawData.gameRtp || typeof rawData.gameRtp !== "number") {
          throw new Error("Invalid or missing gameRtp.");
        }
        if (!Array.isArray(rawData.kenoReelName) || rawData.kenoReelName.length === 0) {
          throw new Error("Invalid or missing kenoReelName array.");
        }

        // Query for the existing GameRtp data by id (same class as regular games)
        const gameRtpQuery = new Parse.Query("GameRtp");
        const queryOutput = await gameRtpQuery.get(params.id, { useMasterKey: true });

        // Update the object fields
        if (rawData.gameName) {
          queryOutput.set("gameName", rawData.gameName);
        }
        if (typeof rawData.gameRtp === "number") {
          queryOutput.set("gameRtp", rawData.gameRtp);
        }
        if (Array.isArray(rawData.kenoReelName)) {
          queryOutput.set("kenoReelName", rawData.kenoReelName); // Different column for Keno
        }

        // Reference gameId from GameCatalogue if provided and it's a new gameId
        if (rawData.gameId && typeof rawData.gameId === 'string') {
          const GameCatalogue = Parse.Object.extend("GameCatalogue");
          const gameCatalogueQuery = new Parse.Query(GameCatalogue);

          try {
            const gameCatalogueObject = await gameCatalogueQuery.get(rawData.gameId);
            if (!gameCatalogueObject) {
              throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}`);
            }
            queryOutput.set("gameId", gameCatalogueObject);
          } catch (error) {
            console.error("GameCatalogue lookup error in update:", error);
            throw new Error(`Invalid gameId: GameCatalogue entry not found for ID: ${rawData.gameId}. Error: ${error.message}`);
          }
        }
        // If gameId is already a pointer object or not provided, skip validation

        // Save the updated GameRtp object with kenoReelName column
        const result = await queryOutput.save(null, { useMasterKey: true });

        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "GameCatalogue") {
        const {
          objectId,
          name,
          category,
          description,
          gameSequence,
          building,
        } = params.data;

        // Validate each field and throw specific errors
        if (!name) {
          throw new Error("Game name is required.");
        }
        if (!category) {
          throw new Error("Category is required.");
        }

        // Fetch the existing GameCatalogue object
        const GameCatalogue = Parse.Object.extend("GameCatalogue");
        const query = new Parse.Query(GameCatalogue);
        const gameObject = await query.get(objectId);

        // Check for duplicate name (excluding current game)
        const duplicateCheck = new Parse.Query(GameCatalogue);
        duplicateCheck.equalTo("name", name);
        duplicateCheck.notEqualTo("objectId", objectId);
        const existingGame = await duplicateCheck.first();
        if (existingGame) {
          throw new Error("Another game with this name already exists.");
        }

        // Update fields
        gameObject.set("name", name);
        gameObject.set("category", category);
        gameObject.set("description", description);
        gameObject.set("gameSequence", Number(gameSequence));

        // Handle building pointer
        if (building) {
          const BuildingId = Parse.Object.extend("VegasBuilding");
          const buildingPointer = new BuildingId();
          buildingPointer.id = building;
          gameObject.set("building", buildingPointer);
        }

        // Save updated object
        const result = await gameObject.save();
        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "wheelSpins") {
        const { id, day, minValue, maxValue, countZero, slots } = params.data;

        if (!day) {
          throw new Error("Day is required.");
        }

        const query = new Parse.Query("SpinningWheel");
        try {
          const spinWheelObject = await query.get(id);

          // Optional: Check for duplicate 'day' if you're enforcing unique days
          const duplicateDayQuery = new Parse.Query("SpinningWheel");
          duplicateDayQuery.equalTo("day", day);
          duplicateDayQuery.notEqualTo("objectId", id); // Exclude current record

          const duplicate = await duplicateDayQuery.first();
          if (duplicate) {
            throw new Error("Another Wheel Spin already exists with this day.");
          }

          spinWheelObject.set("day", day);
          spinWheelObject.set("minValue", minValue);
          spinWheelObject.set("maxValue", maxValue);
          spinWheelObject.set("countZero", countZero);
          spinWheelObject.set("slots", slots);

          const result = await spinWheelObject.save();
          return { data: { id: result.id, ...result.attributes } };
        } catch (error) {
          throw error;
        }
      }
      if (resource === "gameCatalogueSwitch") {
        // Fetch the existing object from Parse
        const GameCatalogue = Parse.Object.extend("GameCatalogue");
        const query = new Parse.Query(GameCatalogue);
        const gameCatalogueObject = await query.get(params.id);

        // Handle isActive switch
        if (params?.data?.isActive !== undefined) {
          gameCatalogueObject.set("isActive", params.data.isActive);
        }

        // Handle isNewGame switch
        if (params?.data?.isNewGame !== undefined) {
          gameCatalogueObject.set("isNewGame", params.data.isNewGame);
        }

        // Handle comingSoon switch
        if (params?.data?.comingSoon !== undefined) {
          gameCatalogueObject.set("comingSoon", params.data.comingSoon);

          // If disabling comingSoon, clear the image URL
          if (!params.data.comingSoon) {
            gameCatalogueObject.set("comingSoonImage", "");
          }
        }

        // Save updated object
        const result = await gameCatalogueObject.save();
        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "gameRtpSwitch") {
        // Fetch the existing object from Parse
        const GameCatalogue = Parse.Object.extend("GameRtp");
        const query = new Parse.Query(GameCatalogue);
        const gameCatalogueObject = await query.get(params.id);

        gameCatalogueObject.set("isActive", params?.data?.isActive);
        // Save updated object
        const result = await gameCatalogueObject.save();
        return { data: { id: result.id, ...result.attributes } };
      }
      if (resource === "gameSettingSwitch") {
        // Fetch the existing object from Parse
        const GameSettings = Parse.Object.extend("GameSettings");
        const query = new Parse.Query(GameSettings);
        const gameSettingsObject = await query.get(params.id);

        gameSettingsObject.set(
          "isBalanceCapped",
          params?.data?.isBalanceCapped
        );
        // Save updated object
        const result = await gameSettingsObject.save();
        return { data: { id: result.id, ...result.attributes } };
      } else {
        // console.log(params);
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        obj = await query.get(params.id);

        // Save other resources directly
        r = await obj.save(data);
      }

      return { data: { id: r.id, ...r.attributes } };
    } catch (error) {
      console.error("Error updating resource:", error);
      throw new Error(error.message);
    }
  },
  updateMany: async (resource, params) => {
    const Resource = Parse.Object.extend(resource);

    const updateData = { ...params.data };

    try {
      const objects = await Promise.all(
        params.ids.map((id) => new Parse.Query(Resource).get(id))
      );

      const updatedObjects = await Promise.all(
        objects.map(async (obj) => {
          Object.entries(updateData).forEach(([key, value]) => {
            obj.set(key, value);
          });
          return await obj.save();
        })
      );

      return { data: updatedObjects.map((obj) => obj.id) };
    } catch {
      throw Error("Failed to update all");
    }
  },
  delete: async (resource, params) => {
    var query = null;
    var result = null;
    var data = null;
    // console.log("DELETE CALLED");
    // console.log(params);
    try {
      if (resource === "users") {
        query = new Parse.Query(Parse.User);
        result = await query.get(params.id, { useMasterKey: true });
        data = { data: { id: result.id, ...result.attributes } };
        await result.destroy({ useMasterKey: true });
      } else if (resource === "gameRtp") {
        try {
          const Resource = Parse.Object.extend("GameRtp");
          const query = new Parse.Query(Resource);
          result = await query.get(params?.record?.id);

          await result.destroy();

          return {
            data: { id: result.id, message: "Record deleted successfully" },
          };
        } catch (error) {
          console.error("Error deleting GameRtp record:", error);
        }
      } else if (resource === "wheelSpins") {
        try {
          const Resource = Parse.Object.extend("SpinningWheel");
          const query = new Parse.Query(Resource);
          result = await query.get(params?.record?.id);

          await result.destroy();

          return {
            data: { id: result.id, message: "Record deleted successfully" },
          };
        } catch (error) {
          console.error("Error deleting SpinningWheel record:", error);
        }
      } else {
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        result = await query.get(params.id);
        data = { data: { id: result.id, ...result.attributes } };
        await result.destroy();
      }
      return data;
    } catch (error) {
      throw Error("Unable to delete");
    }
  },
  deleteMany: async (resource, params) => {
    const Resource = Parse.Object.extend(resource);
    try {
      const qs = await Promise.all(
        params.ids.map((id) => new Parse.Query(Resource).get(id))
      );
      await Promise.all(qs.map((obj) => obj.destroy()));
      return { data: params.ids };
    } catch (error) {
      throw Error("Unable to delete all");
    }
  },
  userCount: async (resource, params) => {
    try {
      const currentUser = Parse.User.current();
      const currentUserId = currentUser?.id; // may be undefined

      const thisMonthStart = new Date();
      thisMonthStart.setDate(1);
      thisMonthStart.setHours(0, 0, 0, 0);

      const lastMonthStart = new Date(thisMonthStart);
      lastMonthStart.setMonth(thisMonthStart.getMonth() - 1);

      const lastMonthEnd = new Date(thisMonthStart); // start of this month

      // This month
      const thisMonthQuery = new Parse.Query(Parse.User)
        .greaterThanOrEqualTo("createdAt", thisMonthStart);
      if (currentUserId) thisMonthQuery.notEqualTo("objectId", currentUserId);
      const thisMonthCount = await thisMonthQuery.count();

      // Last month
      const lastMonthQuery = new Parse.Query(Parse.User)
        .greaterThanOrEqualTo("createdAt", lastMonthStart)
        .lessThan("createdAt", lastMonthEnd);
      if (currentUserId) lastMonthQuery.notEqualTo("objectId", currentUserId);
      const lastMonthCount = await lastMonthQuery.count();

      // Suspended this month
      const suspendedQuery = new Parse.Query(Parse.User)
        .greaterThanOrEqualTo("suspendedDate", thisMonthStart)
        .equalTo("isActive", false);
      if (currentUserId) suspendedQuery.notEqualTo("objectId", currentUserId);
      const suspendedCount = await suspendedQuery.count();

      return [
        { id: 1, name: "This Month",  count: `${thisMonthCount} new users`, color: "#00FF95" },
        { id: 2, name: "Last Month",  count: `${lastMonthCount} new users`, color: "#FFFFFF" },
        { id: 3, name: "Suspended This Month", count: `${suspendedCount} users`, color: "#FF4500" },
      ];
    } catch (error) {
      console.error("Error fetching user counts:", error);
      throw error;
    }
  },

  updateTicket: async (params) => {
    const { ticketId, status, remarks } = params;

    try {
      // Validate input data
      const { validateTicketUpdate } = await import("../Validators/ticket.validator");
      const validation = validateTicketUpdate({
        ticketId,
        status,
        remarks,
      });

      if (!validation.isValid) {
        return {
          success: false,
          message: "Validation failed",
          errors: validation.errors,
        };
      }

      // Fetch the ticket record
      const Ticket = Parse.Object.extend("Ticket");
      const query = new Parse.Query(Ticket);
      query.equalTo("objectId", ticketId);
      const ticket = await query.first({ useMasterKey: true });

      if (!ticket) {
        return {
          success: false,
          message: `Ticket with ID ${ticketId} not found`,
        };
      }

      // Update ticket fields
      if (status) {
        ticket.set("status", status.toLowerCase());
      }

      if (remarks !== undefined && remarks !== null) {
        ticket.set("remarks", remarks.trim());
      }

      ticket.set("updatedAt", new Date());

      // Save the updated ticket
      await ticket.save(null, { useMasterKey: true });

      return {
        success: true,
        message: "Ticket updated successfully",
        data: {
          id: ticket.id,
          ticketId: ticket.id,
          userId: ticket.get("userId"),
          username: ticket.get("username"),
          role: ticket.get("role"),
          category: ticket.get("category"),
          description: ticket.get("description"),
          remarks: ticket.get("remarks"),
          status: ticket.get("status"),
          attachmentURL: ticket.get("attachmentURL"),
          createdAt: ticket.createdAt,
          updatedAt: ticket.updatedAt,
        },
      };
    } catch (error) {
      console.error("Error updating ticket:", error);
      return {
        success: false,
        message:
          error.message ||
          "An unexpected error occurred while updating the ticket",
      };
    }
  },
  
};
export async function getConversionRate(toRole) {
  try {
    const currentUserData = localStorage.getItem(
      `Parse/${process.env.REACT_APP_APP_ID}/currentUser`
    );

    if (!currentUserData) {
      throw new Error("No current user data found in localStorage.");
    }

    const currentUser = JSON.parse(currentUserData);
    const fromRolePointer = currentUser?.role; // Get the *pointer* object

    if (!fromRolePointer || !toRole) {
      throw new Error(
        "Role information is missing. Cannot fetch conversion rate."
      );
    }
    const ConversionRates = Parse.Object.extend("ConversionRates");
    const query = new Parse.Query(ConversionRates);

    // Use equalTo with the objectId
    // query.equalTo("fromRole", fromRoleId);  // Correct comparison!
    query.equalTo("toRole", toRole); // Uncomment when toRole is fixed

    const conversionRateObject = await query.first({ useMasterKey: true });

    if (!conversionRateObject) {
      return null;
    }

    const conversionRate = conversionRateObject.get("conversionRate");

    if (conversionRate === undefined) {
      throw new Error(
        "Rate not found in ConversionRates table for this role combination."
      );
    }

    return conversionRate;
  } catch (error) {
    console.error("Error fetching conversion rate:", error);
    return null;
  }
}

// Coming Soon Management Functions
export async function uploadAndUpdateComingSoon(
  gameId,
  imageFile,
  comingSoon
) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    // Convert image file to base64
    const reader = new FileReader();
    const imageDataPromise = new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    const imageData = await imageDataPromise;

    const result = await Parse.Cloud.run("manageComingSoon", {
      action: "uploadAndUpdate",
      gameId: gameId,
      imageData: imageData,
      fileName: imageFile.name,
      fileType: imageFile.type,
      comingSoon: comingSoon,
    });

    return result;
  } catch (error) {
    console.error("Error uploading and updating coming soon:", error);
    throw error;
  }
}

export async function updateComingSoonStatus(gameId, comingSoon) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("manageComingSoon", {
      action: "updateStatus",
      gameId: gameId,
      comingSoon: comingSoon,
    });

    return result;
  } catch (error) {
    console.error("Error updating coming soon status:", error);
    throw error;
  }
}

// Game Promotion Manager Functions
export async function getPromotionData() {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "getData"
    });

    return result;
  } catch (error) {
    console.error("Error fetching promotion data:", error);
    throw error;
  }
}

export async function getAvailableGames() {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "getGames"
    });

    return result;
  } catch (error) {
    console.error("Error fetching available games:", error);
    throw error;
  }
}

export async function addGameToPromotion(gameId, section) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "addGame",
      gameId: gameId,
      section: section
    });

    return result;
  } catch (error) {
    console.error("Error adding game to promotion:", error);
    throw error;
  }
}

export async function addGameToPromotionWithImage(gameId, section, imageFile) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    let result;

    // For Coming Soon, only image upload is required (no gameId needed)
    if (section === "comingSoon") {
      if (!imageFile) {
        throw new Error("Image is required for Coming Soon section");
      }

      // Convert image file to base64
      const reader = new FileReader();
      const imageDataPromise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(imageFile);

      const imageData = await imageDataPromise;

      // Call backend with image upload for Coming Soon (no gameId)
      result = await Parse.Cloud.run("gamePromotionManager", {
        operation: "addGameWithImage",
        section: section,
        imageData: imageData,
        fileName: imageFile.name,
        fileType: imageFile.type
      });
    } else {
      // For New Launch, gameId is required
      if (!gameId) {
        throw new Error("Game selection is required for New Launch section");
      }

      if (imageFile) {
        // Convert image file to base64
        const reader = new FileReader();
        const imageDataPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
        reader.readAsDataURL(imageFile);

        const imageData = await imageDataPromise;

        // Call backend with image upload
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "addGameWithImage",
          gameId: gameId,
          section: section,
          imageData: imageData,
          fileName: imageFile.name,
          fileType: imageFile.type
        });
      } else {
        // Call backend without image
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "addGame",
          gameId: gameId,
          section: section
        });
      }
    }

    return result;
  } catch (error) {
    console.error("Error adding game to promotion with image:", error);
    throw error;
  }
}

export async function updateGameInPromotion(gameData) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    let result;

    // For Coming Soon, only image updates are allowed (no gameId changes)
    if (gameData.gameCategory === "comingSoon") {
      if (gameData.popUpImage) {
        // Convert image file to base64
        const reader = new FileReader();
        const imageDataPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
        reader.readAsDataURL(gameData.popUpImage);

        const imageData = await imageDataPromise;

        // Call backend with image upload for Coming Soon update
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "updateGameWithImage",
          originalGameId: gameData.originalGameId,
          originalSection: gameData.originalSection,
          newSection: gameData.gameCategory,
          imageData: imageData,
          fileName: gameData.popUpImage.name,
          fileType: gameData.popUpImage.type
        });
      } else {
        // Call backend without new image for Coming Soon
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "updateGame",
          originalGameId: gameData.originalGameId,
          originalSection: gameData.originalSection,
          newSection: gameData.gameCategory,
          keepCurrentImage: !!gameData.currentPopUpImageUrl
        });
      }
    } else {
      // For New Launch, gameId is required
      if (!gameData.gameId) {
        throw new Error("Game selection is required for New Launch section");
      }

      if (gameData.popUpImage) {
        // Convert image file to base64
        const reader = new FileReader();
        const imageDataPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
        reader.readAsDataURL(gameData.popUpImage);

        const imageData = await imageDataPromise;

        // Call backend with image upload for update
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "updateGameWithImage",
          originalGameId: gameData.originalGameId,
          originalSection: gameData.originalSection,
          newGameId: gameData.gameId,
          newSection: gameData.gameCategory,
          imageData: imageData,
          fileName: gameData.popUpImage.name,
          fileType: gameData.popUpImage.type
        });
      } else {
        // Call backend without new image
        result = await Parse.Cloud.run("gamePromotionManager", {
          operation: "updateGame",
          originalGameId: gameData.originalGameId,
          originalSection: gameData.originalSection,
          newGameId: gameData.gameId,
          newSection: gameData.gameCategory,
          keepCurrentImage: !!gameData.currentPopUpImageUrl
        });
      }
    }

    return result;
  } catch (error) {
    console.error("Error updating game in promotion:", error);
    throw error;
  }
}

export async function removeGameFromPromotion(gameId, section) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "removeGame",
      gameId: gameId,
      section: section
    });

    return result;
  } catch (error) {
    console.error("Error removing game from promotion:", error);
    throw error;
  }
}

export async function setPromotionPriority(prioritySection) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "setPriority",
      prioritySection: prioritySection
    });

    return result;
  } catch (error) {
    console.error("Error setting promotion priority:", error);
    throw error;
  }
}

export async function updateGameDisplayOrder(orderData) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("gamePromotionManager", {
      operation: "updateDisplayOrder",
      orderData: orderData
    });

    return result;
  } catch (error) {
    console.error("Error updating game display order:", error);
    throw error;
  }
}

// Transaction Reports Functions
export async function getReportsSummary(params) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("getReportsSummary", params);
    return result;
  } catch (error) {
    console.error("Error fetching reports summary:", error);
    throw error;
  }
}

export async function getReportsDetails(params) {
  try {
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const result = await Parse.Cloud.run("getReportsDetails", params);
    return result;
  } catch (error) {
    console.error("Error fetching reports details:", error);
    throw error;
  }
}

export const getTicketReport = async (filter) => {
  try {
    const response = await dataProvider.getList("tickets", {
      pagination: { page: 1, perPage: 100000 },
      sort: { field: "createdAt", order: "DESC" },
      filter: filter,
    });
    const statusCounts = response.data.reduce((acc, record) => {
      const status = record.status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return { status: "success", data: statusCounts, total: response?.total };

  } catch (error) {
    console.error("Error fetching Ticket report:", error.message);
    return { status: "error", code: 500, message: error.message };
  }
};

